import {
  RunOverview,
  isIdleRun,
  getAdjustedEndDate,
  getLatestRunEndMillis,
  wasRunInterrupted,
} from "../data/models/RunOverview";

import { ChartOptions, ScriptableContext, Tick } from "chart.js";

import {
  DryingGraphYAxis,
  getComBoxYAxisValue,
  getYAxisMinMax,
  formatYAxisValue,
  getYAxisFormatString,
  getYAxisMaxValueString,
  getSelectedHumidityValue,
  isReducedAxis,
  WeatherInformationDetailsLevel,
} from "@/utils/DryingGraphYAxis";

import { LineAnnotationOptions } from "chartjs-plugin-annotation";

import { DateTime } from "luxon";
import { formatJsDate, formatJsTime } from "../utils/DateFormatter";
import { hasPlaceholderClockDate } from "../utils/PanelClockPlaceholders";
import * as _ from "lodash";
import {
  advanceByMinutes,
  areDatesOnSameDay,
  findDaylightSavings as findDaylightSavingsChangeBetween,
  getNumberDaysBetween,
} from "./DateHelper";
import {
  PanelSampleInfo,
  ComBoxSampleInfo,
  getFloorSampleData,
  getRoomSampleData,
  isPauseStartSample,
  isMonitorSample,
  isRunReachedMaxDurationSample,
  isPauseEndSample,
  hasGuideSensor as isGuideSensorOfComBoxSampleSet,
  hasV2OrGreaterSampleData,
  isUartStatusSample,
  isDryingPauseInfoSample,
  isConfigurationInfoSample,
  isConfigurationTypeInfoSample,
} from "@/data/models/SampleInfo";

import {
  IresDevice,
  getIresDeviceCategory,
  IresDeviceCategory,
  calculateErrorOrWarningTimeIntervalsOfPanel,
  getIresDeviceCategoryFromSerialNumber,
} from "@/data/models/IresDevice";
import {
  COLOR_DRYING_GRAPH_HUMIDITY_WEATHER,
  COLOR_DRYING_GRAPH_MIN_MAX_FILL_HUE_OTHER,
  COLOR_DRYING_GRAPH_MIN_MAX_FILL_HUE_WALL,
  COLOR_DRYING_GRAPH_MIN_MAX_FILL_COLOR_OTHER,
  COLOR_DRYING_GRAPH_MIN_MAX_FILL_COLOR_WALL,
  DRYING_GRAPH_FILL_INTENSITY_DRY,
  DRYING_GRAPH_FILL_INTENSITY_WET,
  DRYING_GRAPH_FILL_ALPHA_MIN_WALL,
  DRYING_GRAPH_FILL_ALPHA_MAX_WALL,
  DRYING_GRAPH_FILL_ALPHA_MIN_OTHER,
  DRYING_GRAPH_FILL_ALPHA_MAX_OTHER,
  DRYING_GRAPH_HIGHLIGHT_DIM_ALPHA,
  hexWithAlpha,
  COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
  COLOR_DRYING_GRAPH_TRANSPARENT_WHITE,
  COLOR_DRYING_GRAPH_PERCIPITATION_WEATHER,
  COLOR_DRYING_GRAPH_PRECIPITATION_FILL,
  COLOR_DRYING_GRAPH_PHASE_INFO_POST_DRYING,
  COLOR_DRYING_GRAPH_PHASE_INFO_WARNING,
  COLOR_DRYING_GRAPH_POWER_CONSUMPTION,
  COLOR_DRYING_GRAPH_PRESSURE_WEATHER,
  COLOR_DRYING_GRAPH_RUN_END,
  COLOR_DRYING_GRAPH_RUN_START,
  COLOR_DRYING_GRAPH_TEMP_WEATHER,
  COLOR_GRAPH_DARK_BLUE,
  COLOR_GRAPH_DARK_RED,
  COLOR_GRAPH_LIGHT_RED,
  PHASE_ANNOTATION_COLORS,
  COLOR_DRYING_GRAPH_REGION_POWER_FAIL,
  COLOR_DRYING_GRAPH_REGION_DRYING_PAUSE,
  COLOR_DRYING_GRAPH_REGION_POWER_FAIL_DARK,
  COLOR_DRYING_GRAPH_REGION_DRYING_PAUSE_DARK,
  COLOR_DRYING_GRAPH_INTENSITY,
  COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_1,
} from "./colors";
import {
  IresSensorAndValueCombination,
  iresSensorAndValueToKey,
  iresSensorAndValueFromKey,
  IresDeviceSensor,
  IresDeviceSensorValue,
  getIresDeviceSensorColor,
  getIresDeviceSensorBaseColor,
  getComBoxSensorForSensorIndex,
} from "@/data/models/IresDeviceSensor";
import {
  ComBoxSensorData,
  ROOM_SENSOR_INDEX,
  TOTAL_SENSOR_COUNT,
} from "@/data/models/ComBoxSensorData";
import { aggregateSensorSlotSerials, calculateSlotNumberFromIndex, ComBox, formatSensorSlotLabel, getSerialForSensorIndex, isHyDryMiniSerial } from "@/data/models/ComBox";
import { getMatchingDryingGraphYAxis } from "@/data/models/ComBoxHumidityUnit";
import { clamp, filterOutUndefined } from "./Utils";
import {
  DryingPhaseInfo,
  findTimeRegionsOfInterestInRuns,
  findTimeRegionsOfInterestInRunsOfPanel,
  getLastSampleDate,
  ProgramChainTimeRegionOfInterestType,
  ProgramChainTimeRegionOfInterestTypeOfPanel,
} from "@/data/models/ProgramChain";
import { isUndefined } from "lodash";
import Panel, { StatusHistory } from "@/data/models/Panel";
import { loadSamplesOfRun } from "@/data/repository/RunRepository";
import { IresDeviceResponseReader } from "./IresDeviceResponseReader";
import { hexToUint8Array } from "./ByteUtils";
import * as base64Buffer from "base64-arraybuffer";
import { viewIntensityAccess, viewPowerInformationAccess, viewWeatherInformationAccess } from "@/permissions/UserPermissions";
import { getComBoxHumidityUnit } from "@/data/repository/SettingsRepository";
import { fetchWeatherInformation } from "@/data/repository/WeatherInformationRepository";
import { PHASE_ANNOTATION_KEY_PREFIX } from "@/utils/DryingGraphCompactOptions";
import { getAxisBoundaryTick } from "@/utils/DryingGraphXAxisBounds";
import { getEffectiveUserLazily } from "@/utils/EffectiveUserLazy";

export const X_AXIS_ID = "X_AXIS";
export const LEFT_Y_AXIS = "LEFT_Y_AXIS";
export const RIGHT_Y_AXIS = "RIGHT_Y_AXIS";
export const RIGHT_Y_AXIS_RESISTANCE = "RIGHT_Y_AXIS_RESISTANCE";

/** Identifies the semantic type of a chart dataset for tooltip grouping and unit resolution. */
export type IresDatasetType =
  | 'sensor-temp'
  | 'sensor-humidity'
  | 'sensor-resistance'
  | 'power'
  | 'weather-temp'
  | 'weather-humidity'
  | 'weather-pressure'
  | 'weather-precipitation'
  | 'panel-temp'
  | 'heat-ratio';

const MINUTES_IN_DAY = 60 * 24;

const TENSION_PANEL = "0.1";
const TENSION_COMBOX = "0.5";
const TENSION_POWER_LINE = "0.0";

const WEATHER_TEMP_COLOR = COLOR_DRYING_GRAPH_TEMP_WEATHER;
const WEATHER_HUMIDITY_COLOR = COLOR_DRYING_GRAPH_HUMIDITY_WEATHER;
const WEATHER_PRESSURE_COLOR = COLOR_DRYING_GRAPH_PRESSURE_WEATHER;
const WEATHER_PERCIPITATION_COLOR = COLOR_DRYING_GRAPH_PERCIPITATION_WEATHER;

const POWER_LINE_COLOR = COLOR_DRYING_GRAPH_POWER_CONSUMPTION;

const WEATHER_INFORMATION_BORDER_WIDTH = 2;

export interface WeatherData {
  date: string;
  timestamp: string;
  temperature: number | null;
  relativeHumidity: number | null;
  pressureMsl: number | null;
  precipitation: number | null;
}

export interface WeatherInformationApiResponse {
  weather: WeatherData[];
}

export interface DryingProgressGraphOptions {
  showDetails?: boolean;
  forcedEndDate?: Date;
  skipAnimation?: boolean;
  hideLimitLines?: boolean;
  powerfailDate?: Date;
  hideRunStartsCausedBeMaxRunDuration?: boolean;
  leftYAxis?: DryingGraphYAxis;
  rightYAxis?: DryingGraphYAxis;
  hidePhaseAnnotations?: boolean;
  addLabelDataSets?: boolean;
}

export interface PowerConsumptionValues {
  globalMaximum: number;
  globalMinimum: number;
  localMaximum: number[];
  localMinimum: number[];
}

interface LimitLine {
  xValue: number;
  color: string;
}

interface ChartDataEntry {
  x: number;
  y: number | null;
  /**
   * The value before the axis clamp, present only when the clamp moved it.
   *
   * `y` has to stay inside the axis or Chart.js draws outside the plot area, but the hover tooltip must
   * report what the sensor sent: a ComBox slot with no sensor reports −20 °C and a stuck one −3.5 °C, and
   * clamping those to the axis floor made the tooltip say "0 °C", which reads as a real measurement. Only
   * set when it differs, so the common point stays two numbers.
   */
  rawY?: number;
}

export interface IresChartOptions extends ChartOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iresInfo: any;
}

export interface SensorValueCombination {
  sensor: IresDeviceSensor;
  values: IresDeviceSensorValue[];
}

class SensorValueCombinationCollector {
  sensorCombinations = new Map<IresDeviceSensor, IresDeviceSensorValue[]>();

  addSensorValueCombination(
    iresDeviceSensor: IresDeviceSensor,
    iresDeviceSensorValue: IresDeviceSensorValue
  ) {
    if (!this.sensorCombinations.has(iresDeviceSensor)) {
      this.sensorCombinations.set(iresDeviceSensor, []);
    }

    const sensorValues = this.sensorCombinations.get(iresDeviceSensor) ?? [];

    if (!sensorValues.includes(iresDeviceSensorValue)) {
      sensorValues.push(iresDeviceSensorValue);
    }
  }

  get sortedSensorCombinations() {
    const result: SensorValueCombination[] = [];

    this.sensorCombinations.forEach((sensorValues, sensor) => {
      result.push({
        sensor: sensor,
        values: sensorValues,
      });
    });

    return result.sort((s1, s2) => s1.sensor - s2.sensor);
  }
}

export interface DryingProgressChartDataMetaData {
  startDate: string;
  endDate: string;
  durationInMinutes: number;
  maxTicksLimit?: number;

  xAxisMinValue: number;
  xAxisMaxValue: number;

  leftYAxisMinValue: number;
  leftYAxisMaxValue: number;
  leftYAxisStepsSize: number;
  leftYAxisFormatString: string;
  leftYAxisMaxValueString: string;
  leftYAxisColor: string;

  rightYAxisMinValue: number;
  rightYAxisMaxValue: number;
  rightYAxisStepsSize: number;
  rightYAxisFormatString: string;
  rightYAxisMaxValueString: string;
  rightYAxisColor: string;

  limitLines: LimitLine[];
}

export interface DryingProgressChartData {
  chartData: object;
  options: object;
  metaData: DryingProgressChartDataMetaData;
  sensorCombinations: SensorValueCombination[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  powerConsumptionValues?: any;
  /** True when weather data was requested but all weather sources failed. */
  weatherDataLoadFailed?: boolean;
}

enum TimeScaleUnit {
  MINUTES,
  HOURS,
  DAYS,
}

export function toggleWeatherInformationDetailsLevel(
  weatherInformationDetailsLevel: WeatherInformationDetailsLevel
) {
  switch (weatherInformationDetailsLevel) {
    case WeatherInformationDetailsLevel.NONE:
      return WeatherInformationDetailsLevel.TEMP_AND_HUMIDITY;
    case WeatherInformationDetailsLevel.TEMP_AND_HUMIDITY:
      return WeatherInformationDetailsLevel.ALL;
    case WeatherInformationDetailsLevel.ALL:
      return WeatherInformationDetailsLevel.NONE;
  }
}

/** A copy of `date` without its seconds; `date` itself is left as it is. */
function removeSecondsAndMilliseconds(date: Date) {
  const copy = new Date(date.getTime());

  copy.setSeconds(0);
  copy.setMilliseconds(0);

  return copy;
}

export class TimeScale {
  timeScaleUnit!: TimeScaleUnit;

  startDate: DateTime;
  endDate: DateTime;

  daylightSavingThreshold: number;
  offsetChange: number;

  daysMultiplier = 1;

  constructor(
    startDate: DateTime,
    endDate: DateTime,
    durationInMinutes: number
  ) {
    this.startDate = startDate;
    this.endDate = endDate;

    this.offsetChange = this.endDate.offset - this.startDate.offset;

    const daylightSavingsChangeBetween = findDaylightSavingsChangeBetween(
      startDate,
      endDate
    );

    if (daylightSavingsChangeBetween) {
      // Callers pass minutes since midnight of the start day, not since the start, so the
      // threshold counts from there too. Counting from the start drew the hours just before the
      // change (as many as the start's time of day) an hour early (BB280, 2026-10-25).
      this.daylightSavingThreshold =
        startDate.hour * 60 +
        startDate.minute +
        (daylightSavingsChangeBetween.toMillis() - startDate.toMillis()) /
          (60 * 1000);
    } else {
      this.daylightSavingThreshold = Number.MAX_SAFE_INTEGER;
    }

    if (durationInMinutes < 180) {
      this.timeScaleUnit = TimeScaleUnit.MINUTES;
    } else if (durationInMinutes < MINUTES_IN_DAY * 2) {
      this.timeScaleUnit = TimeScaleUnit.HOURS;
    } else {
      this.timeScaleUnit = TimeScaleUnit.DAYS;

      const numDays = Math.floor(durationInMinutes / MINUTES_IN_DAY);
      this.daysMultiplier = Math.ceil(numDays / 10);
    }
  }

  scaleToMinutes(value: number) {
    switch (this.timeScaleUnit) {
      case TimeScaleUnit.MINUTES:
        return value;
      case TimeScaleUnit.HOURS:
        return value * 60;
      case TimeScaleUnit.DAYS:
        return value * MINUTES_IN_DAY * this.daysMultiplier;
    }
  }

  scaleMinutes(minutes: number, ignoreOffset?: boolean) {
    let adjustedMintues = minutes;

    if (!ignoreOffset && minutes > this.daylightSavingThreshold) {
      adjustedMintues += this.offsetChange;
    }

    switch (this.timeScaleUnit) {
      case TimeScaleUnit.MINUTES:
        return adjustedMintues;
      case TimeScaleUnit.HOURS:
        return adjustedMintues / 60;
      case TimeScaleUnit.DAYS:
        return adjustedMintues / (MINUTES_IN_DAY * this.daysMultiplier);
    }
  }
}

/**
 * `y` for a plotted point, plus `rawY` when the axis clamp moved the value.
 *
 * Spread into the point literal (`{ x, ...clampedValue(v, min, max) }`) so a clamped series still knows what
 * it was asked to plot. See {@link ChartDataEntry.rawY}.
 */
function clampedValue(
  value: number | null,
  min: number,
  max: number
): { y: number | null; rawY?: number } {
  const y = clamp(value, min, max);

  return value !== null && y !== value ? { y, rawY: value } : { y };
}

function showSensorValue(
  targetSensorKeys: Set<number>,
  sensor: IresDeviceSensor,
  value: IresDeviceSensorValue
) {
  return targetSensorKeys.has(
    iresSensorAndValueToKey({
      sensor: sensor,
      value: value,
    })
  );
}

/**
 * Maps a drying-intensity value (heatRatio %) to a fill alpha, linearly across
 * the [DRY..WET] intensity window (clamped). Below DRY -> minAlpha (driest,
 * faintest); above WET -> maxAlpha (wettest, strongest). The window amplifies the
 * narrow real operating range so the wet->dry fade is visible instead of flat.
 */
function intensityToFillAlpha(
  intensity: number,
  minAlpha: number,
  maxAlpha: number
): number {
  const span = DRYING_GRAPH_FILL_INTENSITY_WET - DRYING_GRAPH_FILL_INTENSITY_DRY;
  const t = Math.min(
    1,
    Math.max(0, (intensity - DRYING_GRAPH_FILL_INTENSITY_DRY) / span)
  );
  return minAlpha + (maxAlpha - minAlpha) * t;
}

/**
 * Whether a dataset is the one currently highlighted by the legend/line hover.
 * `highlightColors` holds either sensor line-color strings (matched against the
 * dataset's `borderColor`, excluding weather/power which are color-shared) or
 * dataset-type identifiers (matched against `iresDatasetType`). Shared by the
 * line dim (globalAlpha plugin in DryingProgressGraph.vue) and the band-fill dim
 * (below) so both fade in step. Returns true when no hover is active.
 */
export function isIresDatasetHighlighted(
  borderColor: string,
  datasetType: string,
  highlightColors: string[] | null | undefined
): boolean {
  if (!highlightColors || highlightColors.length === 0) {
    return true;
  }

  const isColorMatch =
    highlightColors.includes(borderColor) &&
    !datasetType.startsWith("weather-") &&
    datasetType !== "power";
  const isTypeMatch = highlightColors.includes(datasetType);

  return isColorMatch || isTypeMatch;
}

/**
 * Builds a scriptable Chart.js `backgroundColor` for a panel temperature band
 * whose opacity encodes drying intensity (heatRatio %). The hue stays fixed; only
 * the alpha varies along the time axis, so the band fades as the wall dries
 * (lower intensity = drier = fainter). Returns a horizontal `CanvasGradient`
 * spanning the plot area, with one color stop per intensity sample.
 *
 * This surfaces the dryness signal to every user, including those without
 * `viewIntensityAccess` who never see the intensity line/number, without exposing
 * the raw value. Falls back to the flat fill while the chart layout is not ready
 * yet or when no intensity data exists (e.g. idle runs).
 */
function buildIntensityBandBackground(
  baseHue: string,
  minAlpha: number,
  maxAlpha: number,
  flatFallback: string,
  fillIntensityEntries: Array<ChartDataEntry>
) {
  const sortedPoints = fillIntensityEntries
    .filter((entry): entry is { x: number; y: number } => entry.y !== null)
    .sort((a, b) => a.x - b.x);

  return (context: ScriptableContext<"scatter">) => {
    const chart = context.chart;
    const chartArea = chart.chartArea;
    const xScale = chart.scales[X_AXIS_ID];

    // Dim the fill in step with the line-dim when the user hovers a different
    // sensor. The hovered state is stashed on the chart by DryingProgressGraph.vue
    // (the line dim uses globalAlpha, which the built-in Filler draws before, so
    // the fill has to carry its own dim). No hover -> factor 1 (no change).
    const highlightColors = (chart as unknown as {
      $iresHighlightColors?: string[] | null;
    }).$iresHighlightColors;
    const dataset = chart.data.datasets[context.datasetIndex] as {
      borderColor?: string;
      iresDatasetType?: string;
    };
    const dimFactor = isIresDatasetHighlighted(
      dataset?.borderColor ?? "",
      dataset?.iresDatasetType ?? "",
      highlightColors
    )
      ? 1
      : DRYING_GRAPH_HIGHLIGHT_DIM_ALPHA;

    if (!chartArea || !xScale || sortedPoints.length === 0) {
      return flatFallback;
    }

    const { left, right } = chartArea;
    const width = right - left;

    if (width <= 0) {
      return flatFallback;
    }

    const gradient = chart.ctx.createLinearGradient(left, 0, right, 0);

    let previousOffset = -1;
    for (const point of sortedPoints) {
      const pixel = xScale.getPixelForValue(point.x);
      let offset = Math.min(1, Math.max(0, (pixel - left) / width));

      // addColorStop offsets must be non-decreasing; nudge duplicates so a stop
      // is never lost (e.g. off-screen points that clamp to the same edge).
      if (offset <= previousOffset) {
        offset = Math.min(1, previousOffset + 0.0001);
      }
      previousOffset = offset;

      const alpha =
        intensityToFillAlpha(point.y, minAlpha, maxAlpha) * dimFactor;
      gradient.addColorStop(offset, hexWithAlpha(baseHue, alpha));
    }

    return gradient;
  };
}

function buildTemperatureDataSet(
  isWallTemp: boolean,
  spanGaps: boolean,
  showDetails: boolean,
  tempEntries: Array<ChartDataEntry>,
  tempEntriesMaxStep: Array<ChartDataEntry>,
  fillIntensityEntries: Array<ChartDataEntry>
) {
  if (tempEntries.length === 0) {
    return null;
  }

  const sensor = isWallTemp
    ? IresDeviceSensor.PANEL_WALL
    : IresDeviceSensor.PANEL_GROUND;
  const baseColor = getIresDeviceSensorBaseColor(
    sensor,
    IresDeviceSensorValue.TEMPERATURE
  );
  const borderColor = getIresDeviceSensorColor(
    sensor,
    IresDeviceSensorValue.TEMPERATURE
  );

  const fillBackground = buildIntensityBandBackground(
    isWallTemp
      ? COLOR_DRYING_GRAPH_MIN_MAX_FILL_HUE_WALL
      : COLOR_DRYING_GRAPH_MIN_MAX_FILL_HUE_OTHER,
    isWallTemp
      ? DRYING_GRAPH_FILL_ALPHA_MIN_WALL
      : DRYING_GRAPH_FILL_ALPHA_MIN_OTHER,
    isWallTemp
      ? DRYING_GRAPH_FILL_ALPHA_MAX_WALL
      : DRYING_GRAPH_FILL_ALPHA_MAX_OTHER,
    isWallTemp
      ? COLOR_DRYING_GRAPH_MIN_MAX_FILL_COLOR_WALL
      : COLOR_DRYING_GRAPH_MIN_MAX_FILL_COLOR_OTHER,
    fillIntensityEntries
  );

  const showPoints = !showDetails && tempEntriesMaxStep.length === 1;

  return {
    max: {
      normalized: true,
      spanGaps: spanGaps,
      yAxisID: LEFT_Y_AXIS,
      pointRadius: showPoints ? 5 : 0,
      pointHoverRadius: 0,
      backgroundColor: fillBackground,
      borderColor: borderColor,
      xAxisID: X_AXIS_ID,
      showLine: true,
      cubicInterpolationMode: "default",
      tension: TENSION_PANEL,
      fill: "+1",
      data: tempEntriesMaxStep.sort((a, b) => a.x - b.x),
      iresDatasetType: 'panel-temp' as IresDatasetType,
      iresBaseColor: baseColor,
    },
    min: {
      normalized: true,
      yAxisID: LEFT_Y_AXIS,
      pointRadius: showPoints ? 5 : 0,
      pointHoverRadius: 0,
      backgroundColor: COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
      borderColor: borderColor,
      spanGaps: spanGaps,
      xAxisID: X_AXIS_ID,
      showLine: true,
      cubicInterpolationMode: "default",
      tension: TENSION_PANEL,
      data: tempEntries.sort((a, b) => a.x - b.x),
      iresDatasetType: 'panel-temp' as IresDatasetType,
      iresBaseColor: baseColor,
    },
  };
}

function buildWeatherDataSet(
  tempEntries: Array<ChartDataEntry>,
  color: string,
  showDetails: boolean,
  leftYAxis?: boolean,
  borderWidth?: number,
  iresDatasetType?: IresDatasetType,
) {
  if (tempEntries.length === 0) {
    return null;
  }

  const showPoints = !showDetails && tempEntries.length === 1;
  const finalBorderWidth = borderWidth ?? 3;

  return {
    normalized: true,
    yAxisID: leftYAxis === false ? RIGHT_Y_AXIS : LEFT_Y_AXIS,
    borderWidth: finalBorderWidth,
    pointRadius: showPoints ? 5 : 0,
    pointHoverRadius: 0,
    backgroundColor: COLOR_DRYING_GRAPH_TRANSPARENT_WHITE,
    borderColor: color,
    xAxisID: X_AXIS_ID,
    showLine: true,
    spanGaps: true,
    cubicInterpolationMode: "default",
    tension: TENSION_PANEL,
    fill: "+1",
    data: tempEntries.sort((a, b) => a.x - b.x),
    borderDash: [0, finalBorderWidth * 2],
    borderCapStyle: 'round',
    iresDatasetType,
  };
}

function buildComBoxDataSet(
  entries: Array<ChartDataEntry>,
  color: string,
  yAxisId: string,
  showDetails: boolean,
  borderWidth?: number,
  isPowerLine?: boolean,
  isWeatherData?: boolean,
  iresDatasetType?: IresDatasetType,
  baseColor?: string,
) {
  let numEntriesWithValues = 0;

  for (let i = 0; numEntriesWithValues < 2 && i < entries.length; i++) {
    const entry = entries[i];

    if (entry.y !== null) {
      numEntriesWithValues++;
    }
  }

  const hasMultipleEntriesWithValues = numEntriesWithValues >= 2;

  if (numEntriesWithValues === 0) {
    return null;
  }

  const showPoints = showDetails && !hasMultipleEntriesWithValues;
  const finalBorderWidth = borderWidth ?? 3;

  return filterOutUndefined({
    normalized: true,
    yAxisID: yAxisId,
    borderWidth: finalBorderWidth,
    borderColor: color,
    xAxisID: X_AXIS_ID,
    pointRadius: showPoints ? 5 : 0,
    pointHoverRadius: 0,
    backgroundColor: COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
    showLine: true,
    spanGaps: true,
    cubicInterpolationMode: "default",
    tension: isPowerLine ? TENSION_POWER_LINE : TENSION_COMBOX,
    data: entries.sort((a, b) => a.x - b.x),
    borderDash: isWeatherData ? [0, finalBorderWidth * 2] : undefined,
    borderCapStyle: isWeatherData ? 'round' : undefined,
    iresDatasetType,
    // Un-themed base color; set only for chartSeriesColor-themed sensor lines so
    // the graph component can re-derive borderColor on a live theme toggle.
    // Weather/power lines omit it (filtered out) and keep their fixed color.
    iresBaseColor: baseColor,
  });
}

function scaleResistanceDigitsToAxis(
  resistanceDigits: number,
  yAxis: DryingGraphYAxis,
): number {
  return isReducedAxis(yAxis) ? resistanceDigits / 5 : resistanceDigits;
}

function buildHeatRatioDataSet(
  entries: Array<ChartDataEntry>,
  spanGaps: boolean
) {
  if (entries.length === 0) {
    return null;
  }

  return {
    yAxisID: RIGHT_Y_AXIS,
    borderColor: getIresDeviceSensorColor(
      IresDeviceSensor.PANEL_WALL,
      IresDeviceSensorValue.INTENSITY
    ),
    normalized: true,
    xAxisID: X_AXIS_ID,
    pointRadius: 0,
    pointHoverRadius: 0,
    backgroundColor: COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
    showLine: true,
    cubicInterpolationMode: "default",
    tension: TENSION_PANEL,
    spanGaps: spanGaps,
    data: entries.sort((a, b) => a.x - b.x),
    iresDatasetType: 'heat-ratio' as IresDatasetType,
    iresBaseColor: getIresDeviceSensorBaseColor(
      IresDeviceSensor.PANEL_WALL,
      IresDeviceSensorValue.INTENSITY
    ),
  };
}

function extractDryingProgressGraphDataFromComBoxRun(
  targetSensorKeys: Set<number>,

  humidityYAxis: DryingGraphYAxis,
  leftYAxis: DryingGraphYAxis,
  rightYAxis: DryingGraphYAxis,
  resistanceYAxis: DryingGraphYAxis,

  offset: number,
  runOverview: RunOverview,

  addBreak: boolean,
  timeScale: TimeScale,

  roomTempEntries: ChartDataEntry[],
  roomHumidityEntries: ChartDataEntry[],
  roomResistanceEntries: ChartDataEntry[],

  floorTempEntries: ChartDataEntry[][],
  floorHumidityEntries: ChartDataEntry[][],
  floorResistanceEntries: ChartDataEntry[][],

  floorTempGuideEntries: ChartDataEntry[],
  floorHumidityGuideEntries: ChartDataEntry[],
  floorResistanceGuideEntries: ChartDataEntry[],

  averagePowerConsumptionEntries: ChartDataEntry[],

  weatherTempEntries: ChartDataEntry[],
  weatherHumidityEntries: ChartDataEntry[],
  weatherPressureEntries: ChartDataEntry[],
  weatherPrecipitationEntries: ChartDataEntry[],

  showPowerLine?: boolean,
  weatherData?: WeatherData[][],

  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel
) {
  const samples = runOverview.samples;

  if (samples.length === 0) {
    return;
  }

  const tempMinMax = getYAxisMinMax(leftYAxis);
  const humidityMinMax = getYAxisMinMax(rightYAxis);
  const resistanceMinMax = getYAxisMinMax(resistanceYAxis);
  const pressureMinMax = getYAxisMinMax(rightYAxis);
  const precipitationMinMax = getYAxisMinMax(rightYAxis);

  for (let i = 0; i < TOTAL_SENSOR_COUNT; i++) {
    floorTempEntries.push([]);
    floorHumidityEntries.push([]);
    floorResistanceEntries.push([]);
  }

  if (addBreak) {
    const gapX = timeScale.scaleMinutes(offset - 1);

    const gapEntry = {
      x: gapX,
      y: null,
    };

    for (let i = 0; i < TOTAL_SENSOR_COUNT; i++) {
      floorTempEntries[i].push(gapEntry);
      floorHumidityEntries[i].push(gapEntry);
      floorResistanceEntries[i].push(gapEntry);
    }

    roomHumidityEntries.push(gapEntry);
    roomTempEntries.push(gapEntry);
    roomResistanceEntries.push(gapEntry);

    floorHumidityGuideEntries.push(gapEntry);
    floorTempGuideEntries.push(gapEntry);
    floorResistanceGuideEntries.push(gapEntry);
  }

  const runDuration = runOverview.durationInMin;

  let previousTime = -10000;

  let minTimeDiffInMin = 3 * 60;

  if (runDuration < 60) {
    minTimeDiffInMin = 5;
  } else if (runDuration < 3 * 60) {
    minTimeDiffInMin = 10;
  } else if (runDuration < 12 * 60) {
    minTimeDiffInMin = 30;
  } else if (runDuration < 24 * 60) {
    minTimeDiffInMin = 60;
  } else if (runDuration < 2 * 24 * 60) {
    minTimeDiffInMin = 2 * 60;
  } else if (runDuration < 7 * 24 * 60) {
    minTimeDiffInMin = 8 * 60;
  }

  const minTimeDiffMonitorSamplesInMin = minTimeDiffInMin;

  const filteredSamples: ComBoxSampleInfo[] = [];
  let indexOfLastSampleWithSensorData = samples.length - 1;

  for (
    ;
    indexOfLastSampleWithSensorData >= 0;
    indexOfLastSampleWithSensorData--
  ) {
    const comBoxSampleInfo = samples[
      indexOfLastSampleWithSensorData
    ] as ComBoxSampleInfo;

    if (
      comBoxSampleInfo.sensorData.some(
        (sensorData) =>
          sensorData.temperature !== undefined ||
          sensorData.relativeHumidity !== undefined
      )
    ) {
      break;
    }
  }

  runOverview.samples.forEach((sample, index) => {
    const comBoxSampleInfo = sample as ComBoxSampleInfo;

    const isPauseStart = isPauseStartSample(comBoxSampleInfo);
    const isPauseEnd = isPauseEndSample(comBoxSampleInfo);

    const isUartStatus = isUartStatusSample(comBoxSampleInfo);
    const isDryingPauseInfo = isDryingPauseInfoSample(comBoxSampleInfo);
    const isConfigurationInfo = isConfigurationInfoSample(comBoxSampleInfo);
    const isConfigurationTypeInfo = isConfigurationTypeInfoSample(comBoxSampleInfo);

    const isSpecialEvent = 
      isUartStatus || 
      isDryingPauseInfo || 
      comBoxSampleInfo.event === 70 ||
      isConfigurationInfo ||
      isConfigurationTypeInfo;

    const isMonitorOrMonitorEndSample =
      isMonitorSample(comBoxSampleInfo) || comBoxSampleInfo.event === 23;

    const minTimeDiff = isMonitorOrMonitorEndSample
      ? runOverview.hasFilteredMonitorSamples
        ? 0
        : minTimeDiffMonitorSamplesInMin
      : minTimeDiffInMin;

    if (
      (
        isPauseStart ||
        isPauseEnd ||
        index == 0 ||
        index == indexOfLastSampleWithSensorData ||
        sample.sampleTimeInMinutes - previousTime >= minTimeDiff
      ) &&
      !isSpecialEvent
    ) {
      filteredSamples.push(comBoxSampleInfo);
      previousTime = sample.sampleTimeInMinutes;
    }
  });

  const allSensorIndices = Array.from({ length: TOTAL_SENSOR_COUNT }, (_, i) => i);

  const showTempOfSensor = allSensorIndices.map((sensorIndex) =>
    showSensorValue(
      targetSensorKeys,
      getComBoxSensorForSensorIndex(sensorIndex),
      IresDeviceSensorValue.TEMPERATURE
    )
  );

  const showHumidityOfSensor = allSensorIndices.map((sensorIndex) =>
    showSensorValue(
      targetSensorKeys,
      getComBoxSensorForSensorIndex(sensorIndex),
      IresDeviceSensorValue.RELATIVE_HUMIDITY
    )
  );

  const showResistanceOfSensor = allSensorIndices.map((sensorIndex) =>
    showSensorValue(
      targetSensorKeys,
      getComBoxSensorForSensorIndex(sensorIndex),
      IresDeviceSensorValue.RESISTANCE_DIGITS
    )
  );

  let showGuideTemp = showSensorValue(
    targetSensorKeys,
    IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
    IresDeviceSensorValue.TEMPERATURE
  );

  let showGuideHumidity = showSensorValue(
    targetSensorKeys,
    IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
    IresDeviceSensorValue.RELATIVE_HUMIDITY
  );

  let showGuideResistance = showSensorValue(
    targetSensorKeys,
    IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
    IresDeviceSensorValue.RESISTANCE_DIGITS
  );

  if (showGuideHumidity || showGuideTemp || showGuideResistance) {
    //Check if guide sensor data exists
    const hasGuideSensorData = samples.some((sample) => {
      const comBoxSampleInfo = sample as ComBoxSampleInfo;
      return isGuideSensorOfComBoxSampleSet(comBoxSampleInfo);
    });

    if (!hasGuideSensorData) {
      // If not display sensor info of room sensor instead of guide sensor
      showTempOfSensor[ROOM_SENSOR_INDEX] = true;
      showHumidityOfSensor[ROOM_SENSOR_INDEX] = true;

      showGuideTemp = false;
      showGuideHumidity = false;
      showGuideResistance = false;
    }
  }

  let averagePowerConsumptionOfPreviousSample = 0;
  let averagePowerConsumption = 0;

  let lastX = 0;

  let isFirstSample = true;
  let isLastSample = false;

  let globalMaxPowerConsumption = 0;
  let globalMinPowerConsumption = 3600;

  let averageSampleTimeInHoursOfPreviousSample = 0;

  let previousStep = 0;

  const localMaxPowerConsumptionValues: number[] = [];
  const localMinPowerConsumptionValues: number[] = [];
  
  for (const comBoxSampleInfo of filteredSamples) {
    if (comBoxSampleInfo === filteredSamples[filteredSamples.length - 1]) {
      isLastSample = true;
    }

    const pauseStartOrEnd = comBoxSampleInfo.event === 24 || comBoxSampleInfo.event === 25;
    const sampleTimeInHoursOfCurrentSample = comBoxSampleInfo.sampleTimeInMinutes / 60;

    if (pauseStartOrEnd || isFirstSample || isLastSample) {
      const averagePowerConsumptionOfCurrentSample = comBoxSampleInfo.powerConsumption;
      const averageSampleTimeInHours = sampleTimeInHoursOfCurrentSample - averageSampleTimeInHoursOfPreviousSample;
      
      averagePowerConsumption = 
        !isUndefined(averagePowerConsumptionOfCurrentSample) && averagePowerConsumptionOfCurrentSample !== -255 
          ? averageSampleTimeInHours === 0 || (averagePowerConsumptionOfCurrentSample - averagePowerConsumptionOfPreviousSample) < 0
            ? 0
            : ((averagePowerConsumptionOfCurrentSample - averagePowerConsumptionOfPreviousSample) / averageSampleTimeInHours) * 1000 
          : -255;

      averagePowerConsumptionOfPreviousSample = averagePowerConsumptionOfCurrentSample ?? averagePowerConsumptionOfPreviousSample;
      averageSampleTimeInHoursOfPreviousSample = sampleTimeInHoursOfCurrentSample ?? averageSampleTimeInHoursOfPreviousSample;

      if (globalMaxPowerConsumption < averagePowerConsumption) {
        globalMaxPowerConsumption = averagePowerConsumption;
      }

      if (globalMinPowerConsumption > averagePowerConsumption) {
        globalMinPowerConsumption = averagePowerConsumption;
      }

      if (localMaxPowerConsumptionValues[comBoxSampleInfo.step] || localMaxPowerConsumptionValues[comBoxSampleInfo.step] === 0) {
        if (localMaxPowerConsumptionValues[comBoxSampleInfo.step] < averagePowerConsumption) {
          localMaxPowerConsumptionValues[comBoxSampleInfo.step] = averagePowerConsumption;
        }
      }
      else {
        if (previousStep !== comBoxSampleInfo.step && comBoxSampleInfo.step > 0 && localMaxPowerConsumptionValues[comBoxSampleInfo.step-1] < averagePowerConsumption) {
          localMaxPowerConsumptionValues[comBoxSampleInfo.step-1] = averagePowerConsumption;
        }
        
        localMaxPowerConsumptionValues[comBoxSampleInfo.step] = averagePowerConsumption;
      }

      if (localMinPowerConsumptionValues[comBoxSampleInfo.step] || localMinPowerConsumptionValues[comBoxSampleInfo.step] === 0) {
        if (localMinPowerConsumptionValues[comBoxSampleInfo.step] > averagePowerConsumption) {
          localMinPowerConsumptionValues[comBoxSampleInfo.step] = averagePowerConsumption;
        }
      }
      else {
        localMinPowerConsumptionValues[comBoxSampleInfo.step] = averagePowerConsumption;
      }

      if (previousStep !== comBoxSampleInfo.step) {
        previousStep = comBoxSampleInfo.step;
      }
    }

    const roomData = getRoomSampleData(comBoxSampleInfo)[0];
    const x = timeScale.scaleMinutes(
      offset + comBoxSampleInfo.sampleTimeInMinutes
    );

    const averagePowerConsumptionInPercentage = averagePowerConsumption !== -255
      ? averagePowerConsumption >= 3600
        ? 100
        : (averagePowerConsumption / 3600) * 100
      : -255;
    const normalizedAveragePowerConsumptionInPercentage = rightYAxis === DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY || rightYAxis === DryingGraphYAxis.COM_BOX_MIXING_RATIO 
      ? averagePowerConsumptionInPercentage / 5 
      : averagePowerConsumptionInPercentage;

    if (
      (isFirstSample || isLastSample || pauseStartOrEnd) && 
      normalizedAveragePowerConsumptionInPercentage !== -255 && showPowerLine
    ) {
      if (isFirstSample) {
        isFirstSample = false;
      }
      else {
        averagePowerConsumptionEntries.push({
          x: lastX,
          ...clampedValue(normalizedAveragePowerConsumptionInPercentage, 
              humidityMinMax.min,
              humidityMinMax.max
            )
        })
        
      }

      averagePowerConsumptionEntries.push({
        x: x,
        ...clampedValue(normalizedAveragePowerConsumptionInPercentage, 
            humidityMinMax.min,
            humidityMinMax.max
          )
      })

      lastX = x;
    }

    if (roomData) {
      if (
        (comBoxSampleInfo.guideSensor === 4 ||
          showTempOfSensor[ROOM_SENSOR_INDEX]) &&
        roomData.temperature !== undefined &&
        (targetSensorKeys.has(0) || isUndefined(showPowerLine))
      ) {
        roomTempEntries.push({
          x: x,
          ...clampedValue(roomData.temperature, tempMinMax.min, tempMinMax.max),
        });
      }

      if (
        (comBoxSampleInfo.guideSensor === 4 ||
          showHumidityOfSensor[ROOM_SENSOR_INDEX]) &&
        roomData.relativeHumidity !== undefined &&
        (targetSensorKeys.has(1) || isUndefined(showPowerLine))
      ) {
        roomHumidityEntries.push({
          x: x,
          ...clampedValue(
            getComBoxYAxisValue(humidityYAxis, roomData),
            humidityMinMax.min,
            humidityMinMax.max
          ),
        });
      }

      if (
        (comBoxSampleInfo.guideSensor === 4 ||
          showResistanceOfSensor[ROOM_SENSOR_INDEX]) &&
        roomData.resistanceDigits !== undefined
      ) {
        const scaledResistance = scaleResistanceDigitsToAxis(
          roomData.resistanceDigits,
          rightYAxis,
        );
        roomResistanceEntries.push({
          x: x,
          ...clampedValue(
            scaledResistance,
            rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
              ? resistanceMinMax.min
              : humidityMinMax.min,
            rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
              ? resistanceMinMax.max
              : humidityMinMax.max
          ),
        });
      }
    }

    const floorSampleData = getFloorSampleData(comBoxSampleInfo);
    let maxFloorSampleData: ComBoxSensorData | undefined = undefined;

    for (const floorSensorData of floorSampleData) {
      if (
        showTempOfSensor[floorSensorData.sensorIndex] &&
        floorSensorData.temperature !== undefined
      ) {
        floorTempEntries[floorSensorData.sensorIndex].push({
          x: x,
          ...clampedValue(floorSensorData.temperature, tempMinMax.min, tempMinMax.max),
        });
      }

      if (
        showHumidityOfSensor[floorSensorData.sensorIndex] &&
        floorSensorData.relativeHumidity !== undefined
      ) {
        floorHumidityEntries[floorSensorData.sensorIndex].push({
          x: x,
          ...clampedValue(
            getComBoxYAxisValue(humidityYAxis, floorSensorData),
            humidityMinMax.min,
            humidityMinMax.max
          ),
        });
      }

      if (
        showResistanceOfSensor[floorSensorData.sensorIndex] &&
        floorSensorData.resistanceDigits !== undefined
      ) {
        const scaledResistance = scaleResistanceDigitsToAxis(
          floorSensorData.resistanceDigits,
          rightYAxis,
        );
        floorResistanceEntries[floorSensorData.sensorIndex].push({
          x: x,
          ...clampedValue(
            scaledResistance,
            rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
              ? resistanceMinMax.min
              : humidityMinMax.min,
            rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
              ? resistanceMinMax.max
              : humidityMinMax.max
          ),
        });
      }

      if (
        !maxFloorSampleData ||
        (floorSensorData.relativeHumidity !== undefined &&
          floorSensorData.relativeHumidity >
            (maxFloorSampleData.relativeHumidity ?? -1))
      ) {
        maxFloorSampleData = floorSensorData;
      }
    }

    let floorGuideSensorData:
      | ComBoxSensorData
      | undefined = floorSampleData.find(
      (data) => data.sensorIndex === comBoxSampleInfo.guideSensor
    );

    if (
      floorGuideSensorData?.relativeHumidity === undefined &&
      floorGuideSensorData?.resistanceDigits === undefined
    ) {
      floorGuideSensorData = maxFloorSampleData;
    }

    // If the fallback floor sensor also has no useful data, clear it
    // so the room-sensor fallback below triggers instead of silently
    // producing no data points (e.g. guideSensor=16 with no matching slot).
    if (
      floorGuideSensorData &&
      floorGuideSensorData.temperature === undefined &&
      floorGuideSensorData.relativeHumidity === undefined &&
      floorGuideSensorData.resistanceDigits === undefined
    ) {
      floorGuideSensorData = undefined;
    }

    if (floorGuideSensorData) {
      const floorGuideTemperature = floorGuideSensorData.temperature;

      if (showGuideTemp && floorGuideTemperature !== undefined) {
        floorTempGuideEntries.push({
          x: x,
          ...clampedValue(floorGuideTemperature, tempMinMax.min, tempMinMax.max),
        });
      }

      const floorGuideHumidity = getComBoxYAxisValue(
        humidityYAxis,
        floorGuideSensorData
      );

      if (showGuideHumidity && floorGuideHumidity !== null) {
        floorHumidityGuideEntries.push({
          x: x,
          ...clampedValue(floorGuideHumidity, humidityMinMax.min, humidityMinMax.max),
        });
      }

      const floorGuideResistance = floorGuideSensorData.resistanceDigits;

      if (showGuideResistance && floorGuideResistance !== undefined) {
        const scaledResistance = scaleResistanceDigitsToAxis(
          floorGuideResistance,
          rightYAxis,
        );
        floorResistanceGuideEntries.push({
          x: x,
          ...clampedValue(
            scaledResistance,
            rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
              ? resistanceMinMax.min
              : humidityMinMax.min,
            rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
              ? resistanceMinMax.max
              : humidityMinMax.max
          ),
        });
      }
    } else if (showGuideTemp || showGuideHumidity || showGuideResistance) {
      // No external/floor sensor data available for this sample.
      // Fall back to showing room data so the graph isn't empty
      // (e.g. room-sensor-only devices with a bogus guideSensor=0
      // in the first sample that passes the .some() check).
      if (roomData) {
        if (showGuideTemp && roomData.temperature !== undefined) {
          roomTempEntries.push({
            x: x,
            ...clampedValue(roomData.temperature, tempMinMax.min, tempMinMax.max),
          });
        }
        const roomHumidity = getComBoxYAxisValue(humidityYAxis, roomData);
        if (showGuideHumidity && roomHumidity !== null) {
          roomHumidityEntries.push({
            x: x,
            ...clampedValue(roomHumidity, humidityMinMax.min, humidityMinMax.max),
          });
        }

        if (showGuideResistance && roomData.resistanceDigits !== undefined) {
          const scaledResistance = scaleResistanceDigitsToAxis(
            roomData.resistanceDigits,
            rightYAxis,
          );
          roomResistanceEntries.push({
            x: x,
            ...clampedValue(
              scaledResistance,
              rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
                ? resistanceMinMax.min
                : humidityMinMax.min,
              rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
                ? resistanceMinMax.max
                : humidityMinMax.max
            ),
          });
        }
      }
    }
  }

  // Process weather data once per run (outside the sample loop)
  if (weatherData && weatherInformationDetailsLevel !== WeatherInformationDetailsLevel.NONE) {
    weatherData.forEach((dailyWeather) => {
      dailyWeather.forEach((weatherEntry) => {
        const runStartDate = runOverview.startDate.toDate();
        const weatherEntryDate = new Date(weatherEntry.timestamp);

        const runDurationInMilliseconds = runDuration * 60 * 1000;
        const runEndDate = new Date(runStartDate.getTime() + runDurationInMilliseconds);

        if (
          runStartDate <= weatherEntryDate &&
          runEndDate >= weatherEntryDate
        ) {
          const weatherEntryMinutes = (weatherEntryDate.getTime() - runStartDate.getTime()) / (1000 * 60);
          const weatherX = timeScale.scaleMinutes(
            offset + weatherEntryMinutes
          );
  
          if (weatherEntry.temperature != null) {
            weatherTempEntries.push({
              x: weatherX,
              ...clampedValue(weatherEntry.temperature, tempMinMax.min, tempMinMax.max),
            });
          }
  
          if (weatherEntry.relativeHumidity != null && weatherEntry.temperature != null) {
            weatherHumidityEntries.push({
              x: weatherX,
              ...clampedValue(
                getSelectedHumidityValue(
                  humidityYAxis,
                  weatherEntry.relativeHumidity, 
                  weatherEntry.temperature
                ), 
                humidityMinMax.min, 
                humidityMinMax.max
              ),
            });
          }
          
          if (weatherEntry.pressureMsl != null && weatherInformationDetailsLevel === WeatherInformationDetailsLevel.ALL) {
            const pressureMsl = rightYAxis === DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY || rightYAxis === DryingGraphYAxis.COM_BOX_MIXING_RATIO 
              ? (weatherEntry.pressureMsl - 950) / 4 
              : weatherEntry.pressureMsl - 950;

            weatherPressureEntries.push({
              x: weatherX,
              ...clampedValue(
                pressureMsl, 
                pressureMinMax.min, 
                pressureMinMax.max
              ),
            });
          }

          if (weatherEntry.precipitation != null && weatherInformationDetailsLevel === WeatherInformationDetailsLevel.ALL) {
            const precipitation = rightYAxis === DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY || rightYAxis === DryingGraphYAxis.COM_BOX_MIXING_RATIO 
              ? weatherEntry.precipitation * 5 / 4 
              : weatherEntry.precipitation * 5;

            weatherPrecipitationEntries.push({
              x: weatherX,
              ...clampedValue(
                precipitation, 
                precipitationMinMax.min, 
                precipitationMinMax.max
              ),
            });
          }
        }
      });
    });
  }

  return ({
    globalMaximum: globalMaxPowerConsumption,
    globalMinimum: globalMinPowerConsumption,
    localMaximum: localMaxPowerConsumptionValues,
    localMinimum: localMinPowerConsumptionValues,
  })
}

function getPanelUnit16Value(iresDeviceResponseReader: IresDeviceResponseReader) {
  const uint16 =
    iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex] +
    iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex + 1] *
      256;

  iresDeviceResponseReader.skip(2);

  return uint16;
}

async function extractDryingProgressGraphDataFromPanelRun(
  targetSensorKeys: Set<number>,
  offset: number,
  runOverview: RunOverview,
  addBreak: boolean,
  wallTempEntries: Array<ChartDataEntry>,
  heatRatioEntries: Array<ChartDataEntry>,
  fillIntensityEntries: Array<ChartDataEntry>,
  wallTempMaxStepEntries: Array<ChartDataEntry>,
  groundTempEntries: Array<ChartDataEntry>,
  groundTempMaxStepEntries: Array<ChartDataEntry>,
  timeScale: TimeScale,
  serialNumber: string,

  weatherTempEntries: Array<ChartDataEntry>,
  weatherHumidityEntries: Array<ChartDataEntry>,
  weatherPressureEntries: Array<ChartDataEntry>,
  weatherPrecipitationEntries: Array<ChartDataEntry>,

  weatherData?: WeatherData[][],
  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel
) {
  const precipitationMinMax = getYAxisMinMax(DryingGraphYAxis.PANEL_INTENSITY);
  const isActiveRun = !isIdleRun(runOverview);

  const showWallTemp = showSensorValue(
    targetSensorKeys,
    IresDeviceSensor.PANEL_WALL,
    IresDeviceSensorValue.TEMPERATURE
  );

  const showWallIntensity = showSensorValue(
    targetSensorKeys,
    IresDeviceSensor.PANEL_WALL,
    IresDeviceSensorValue.INTENSITY
  ) && (viewIntensityAccess(getEffectiveUserLazily()));

  const showGroundTemp = showSensorValue(
    targetSensorKeys,
    IresDeviceSensor.PANEL_GROUND,
    IresDeviceSensorValue.TEMPERATURE
  );

  if (addBreak) {
    const gapX = timeScale.scaleMinutes(offset - 1);

    if (showWallTemp) {
      wallTempEntries.push({
        x: gapX,
        y: null,
      });
      wallTempMaxStepEntries.push({
        x: gapX,
        y: null,
      });
    }

    if (showWallIntensity) {
      heatRatioEntries.push({
        x: gapX,
        y: null,
      });
    }

    if (showGroundTemp) {
      groundTempEntries.push({
        x: gapX,
        y: null,
      });
      groundTempMaxStepEntries.push({
        x: gapX,
        y: null,
      });
    }
  }

  let timeDiffToBeAdjusted = 0;

  const rawSamples = await loadSamplesOfRun(serialNumber, runOverview.id);
  const iresDeviceResponseReader = new IresDeviceResponseReader();

  for (const rawSampleData of rawSamples) { 
    const data = hexToUint8Array(rawSampleData.encodedSample);
    iresDeviceResponseReader.setNewData(data);
  
    const timeDiffInMinutes = getPanelUnit16Value(
      iresDeviceResponseReader
    );

    if (rawSampleData.sampleIndex === 0) {
      timeDiffToBeAdjusted = timeDiffInMinutes;
      break;
    }
  }

  for (const sampleInfo of runOverview.samples) {
    const panelSampleInfo = sampleInfo as PanelSampleInfo;

    const adjustedSampleTimeInMinutes = panelSampleInfo.sampleTimeInMinutes - timeDiffToBeAdjusted;

    const x = timeScale.scaleMinutes(
      offset + adjustedSampleTimeInMinutes
    );

    if (showWallTemp) {
      wallTempEntries.push({
        x: x,
        y: panelSampleInfo.wallTemp,
      });

      if (
        panelSampleInfo.maxWallTempStep &&
        panelSampleInfo.maxWallTempStep < 255
      ) {
        wallTempMaxStepEntries.push({
          x: x,
          y: panelSampleInfo.maxWallTempStep,
        });
      } else {
        wallTempMaxStepEntries.push({
          x: x,
          y: panelSampleInfo.wallTemp,
        });
      }
    }

    if (showGroundTemp) {
      if (panelSampleInfo.groundTemp && panelSampleInfo.groundTemp < 255) {
        groundTempEntries.push({
          x: x,
          y: panelSampleInfo.groundTemp,
        });
      }

      if (
        panelSampleInfo.maxGroundTempStep &&
        panelSampleInfo.maxGroundTempStep < 255
      ) {
        groundTempMaxStepEntries.push({
          x: x,
          y: panelSampleInfo.maxGroundTempStep,
        });
      } else if (
        panelSampleInfo.groundTemp &&
        panelSampleInfo.groundTemp < 255
      ) {
        groundTempMaxStepEntries.push({
          x: x,
          y: panelSampleInfo.groundTemp,
        });
      }
    }

    if (showWallIntensity) {
      heatRatioEntries.push({
        x: x,
        y:
          isActiveRun && panelSampleInfo.heatRatioPercentage
            ? panelSampleInfo.heatRatioPercentage
            : null,
      });
    }

    // Intensity that tints the temperature band fill. Populated for every user
    // regardless of `showWallIntensity` (legend toggle + viewIntensityAccess):
    // non-IRES users get the dryness signal via fill opacity, never the raw
    // value/line. Only meaningful for active runs.
    fillIntensityEntries.push({
      x: x,
      y:
        isActiveRun && panelSampleInfo.heatRatioPercentage
          ? panelSampleInfo.heatRatioPercentage
          : null,
    });
  }

  if (weatherData && weatherInformationDetailsLevel !== WeatherInformationDetailsLevel.NONE) {
    weatherData.forEach((dailyWeather) => {
      dailyWeather.forEach((weatherEntry) => {
        const runStartDate = runOverview.startDate.toDate();
        const weatherEntryDate = new Date(weatherEntry.timestamp);

        const runDurationInMilliseconds = runOverview.durationInMin * 60 * 1000;
        const runEndDate = new Date(runStartDate.getTime() + runDurationInMilliseconds);

        if (
          runStartDate <= weatherEntryDate &&
          runEndDate >= weatherEntryDate
        ) {
          const weatherEntryMinutes = (weatherEntryDate.getTime() - runStartDate.getTime()) / (1000 * 60);
          const weatherX = timeScale.scaleMinutes(
            offset + weatherEntryMinutes
          );

          if (weatherEntry.temperature != null) {
            weatherTempEntries.push({
              x: weatherX,
              y: weatherEntry.temperature,
            });
          }
          
          if (weatherEntry.relativeHumidity != null) {
            weatherHumidityEntries.push({
              x: weatherX,
              y: weatherEntry.relativeHumidity,
            });
          }
  
          if (weatherEntry.pressureMsl != null && weatherInformationDetailsLevel === WeatherInformationDetailsLevel.ALL) {
            weatherPressureEntries.push({
              x: weatherX,
              y: weatherEntry.pressureMsl - 950,
            });
          }

          if (weatherEntry.precipitation != null && weatherInformationDetailsLevel === WeatherInformationDetailsLevel.ALL) {
            weatherPrecipitationEntries.push({
              x: weatherX,
              ...clampedValue(
                weatherEntry.precipitation * 5,
                precipitationMinMax.min,
                precipitationMinMax.max
              ),
            });
          }
        }
      });
    });
  }
}

/**
 * Calculates the offset in minutes from a given reference date to a specific date and time.
 *
 * @param {number} minutesOfDay - The number of minutes elapsed since the start of the day.
 * @param {Date} referenceDate - The reference date to calculate the offset from.
 * @param {Date} date - The date for which the offset is being calculated.
 * @returns {number} The total offset in minutes from the reference date.
 */
function calculateOffsetToReferenceDate(
  minutesOfDay: number,
  referenceDate: Date,
  date: Date
) {
  const adjustedReferenceDate = removeSecondsAndMilliseconds(referenceDate);
  const adjustedDate = removeSecondsAndMilliseconds(date);

  return (
    minutesOfDay +
    Math.ceil(
      (adjustedDate.getTime() - adjustedReferenceDate.getTime()) / (1000 * 60)
    )
  );
}

/**
 * Where the graph draws the end of a run a power cut followed, as an offset like
 * {@link calculateOffsetToReferenceDate}: at its last reading when the stored end is less than 5
 * minutes after it. The box stores the end up to one sample interval after its last reading, so
 * drawing the stored end leaves an empty strip between the curves and the cut. The run end line and
 * the power cut band both start here, so they meet the curves together (BB360, 2026-10-29).
 */
export function getDrawnRunEndX(
  minutesOfDay: number,
  referenceDate: Date,
  runEndDate: Date,
  lastSampleDate: Date | undefined
) {
  const runEndX = calculateOffsetToReferenceDate(minutesOfDay, referenceDate, runEndDate);

  if (!lastSampleDate) {
    return runEndX;
  }

  const lastSampleX = calculateOffsetToReferenceDate(minutesOfDay, referenceDate, lastSampleDate);

  return runEndX - lastSampleX < 5 ? lastSampleX : runEndX;
}

export function getDateFromXAxisValue(
  startDate: Date,
  value: number,
  timeScale: TimeScale
) {
  const date = new Date(startDate);
  date.setHours(0);
  const valueNumber = Number(value);
  const minutes = timeScale.scaleToMinutes(valueNumber);
  date.setMinutes(minutes);
  return date;
}

function shouldLabelBeDisplayed(labelIndex: number, values: Array<number>) {
  if (labelIndex === 0 || labelIndex === values.length - 1) {
    //Always display first or last label
    return true;
  }

  const completeDistance = values[values.length - 1] - values[0];
  const labelValue = values[labelIndex];

  const minDistance = Math.min(
    labelValue - values[0],
    values[values.length - 1] - labelValue
  );

  const minDistanceThreshold = completeDistance * 0.1;

  const passesMinDistanceThreshold = minDistance > minDistanceThreshold;

  return passesMinDistanceThreshold;
}

function getXAxisLabel(
  valueNumber: number,
  index: number,
  ticks: Tick[],
  startDate: Date,
  durationInMinutes: number,
  timeScale: TimeScale,
  xAxisMinValue: number,
  xAxisMaxValue: number
) {
  const values = ticks.map((tick) => tick.value);
  const date = getDateFromXAxisValue(startDate, valueNumber, timeScale);
  const firstAxisDate = getDateFromXAxisValue(startDate, xAxisMinValue, timeScale);
  const lastAxisDate = getDateFromXAxisValue(startDate, xAxisMaxValue, timeScale);
  const currentYear = new Date().getFullYear();
  const shouldShowYearOnBoundaryLabels =
    firstAxisDate.getFullYear() !== currentYear ||
    lastAxisDate.getFullYear() !== currentYear ||
    firstAxisDate.getFullYear() !== lastAxisDate.getFullYear();

  const { isFirst: isFirstLabel, isLast: isLastLabel } = getAxisBoundaryTick(index, values.length);
  const isFirstOrLastLabel = isFirstLabel || isLastLabel;

  const formatBoundaryLabel = (showTime: boolean) =>
    shouldShowYearOnBoundaryLabels
      ? DateTime.fromJSDate(date).toFormat(showTime ? "dd.MM.yy HH:mm" : "dd.MM.yy")
      : formatJsDate(date, showTime);

  if (timeScale.timeScaleUnit === TimeScaleUnit.DAYS) {
    const hideHalfDay =
      !isFirstOrLastLabel && Math.floor(valueNumber) !== valueNumber;

    if (hideHalfDay) {
      return null;
    }

    if (isFirstOrLastLabel) {
      if (timeScale.scaleMinutes(durationInMinutes) < 7) {
        const adjacentIndex = index === 0 ? 1 : values.length - 2;
        const showOnlyTime =
          shouldLabelBeDisplayed(adjacentIndex, values) &&
          areDatesOnSameDay(
            date,
            getDateFromXAxisValue(startDate, values[adjacentIndex], timeScale)
          );

        if (showOnlyTime && !shouldShowYearOnBoundaryLabels) {
          return formatJsTime(date);
        } else {
          return formatBoundaryLabel(true);
        }
      } else {
        return formatBoundaryLabel(true);
      }
    }
  } else if (isFirstOrLastLabel && values.length > 2) {
    return shouldShowYearOnBoundaryLabels ? formatBoundaryLabel(true) : formatJsTime(date);
  }

  if (shouldLabelBeDisplayed(index, values)) {
    const hasTimeInfo = date.getMinutes() !== 0 || date.getHours() !== 0;
    const showTime = hasTimeInfo || durationInMinutes <= 2 * MINUTES_IN_DAY;
    return formatJsDate(date, showTime);
  }

  return null;
}

function initializePowerConsumptionValues(): PowerConsumptionValues {
  return ({
    globalMaximum: -1,
    globalMinimum: -1,
    localMaximum: [] as number[],
    localMinimum: [] as number[]
  })
}

export function extractComBoxEntries(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datasets: any[],
  targetSensorKeys: Set<number>,

  humidityYAxis: DryingGraphYAxis,
  leftYAxis: DryingGraphYAxis,
  rightYAxis: DryingGraphYAxis,
  rightResistanceYAxisId: string,
  resistanceYAxis: DryingGraphYAxis,

  runs: RunOverview[],

  showDetails: boolean,

  timeScale: TimeScale,
  startDate: Date,
  minutesOfDay: number,

  sensorValueCombinationCollector: SensorValueCombinationCollector,

  showPowerLine?: boolean,
  weatherData?: WeatherData[][],

  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel,

  isMini?: boolean,

  activeFallbackSerials?: Record<string, string>,
) {
  if (runs.length == 0) {
    return;
  }

  const resolvedSerials = aggregateSensorSlotSerials(runs, activeFallbackSerials);

  let currentGroup = [runs[0]];
  const groupedRuns = [];

  runs.slice(1).forEach((run, index) => {
    if (wasRunInterrupted(run, runs.slice(0, index + 1))) {
      groupedRuns.push(currentGroup);
      currentGroup = [];
    }

    currentGroup.push(run);
  });

  groupedRuns.push(currentGroup);

  const powerConsumptionValues = initializePowerConsumptionValues();

  // Determine which value types are entirely hidden in the sensor selection.
  // Weather datasets follow the same toggle — unless all sensor values are hidden,
  // in which case weather data is shown as the sole remaining context.
  const allTempHidden = !Array.from(targetSensorKeys).some(
    (key) => iresSensorAndValueFromKey(key).value === IresDeviceSensorValue.TEMPERATURE
  );
  const allHumidityHidden = !Array.from(targetSensorKeys).some(
    (key) => iresSensorAndValueFromKey(key).value === IresDeviceSensorValue.RELATIVE_HUMIDITY
  );
  const allResistanceHidden = !Array.from(targetSensorKeys).some(
    (key) => iresSensorAndValueFromKey(key).value === IresDeviceSensorValue.RESISTANCE_DIGITS
  );
  const allSensorValuesHidden = allTempHidden && allHumidityHidden && allResistanceHidden;

  groupedRuns.forEach((runGroup) => {
    const roomTempEntries: ChartDataEntry[] = [];
    const roomHumidityEntries: ChartDataEntry[] = [];
    const roomResistanceEntries: ChartDataEntry[] = [];

    const floorTempEntries: ChartDataEntry[][] = [];
    const floorHumidityEntries: ChartDataEntry[][] = [];
    const floorResistanceEntries: ChartDataEntry[][] = [];

    const floorKeyTempEntries: ChartDataEntry[] = [];
    const floorKeyHumidityEntries: ChartDataEntry[] = [];
    const floorKeyResistanceEntries: ChartDataEntry[] = [];

    const averagePowerConsumptionEntries: ChartDataEntry[] = [];
    
    const weatherTempEntries: ChartDataEntry[] = [];
    const weatherHumidityEntries: ChartDataEntry[] = [];
    const weatherPressureEntries: ChartDataEntry[] = [];
    const weatherPrecipitationEntries: ChartDataEntry[] = [];

    runGroup.forEach((runOverview) => {
      const offset = calculateOffsetToReferenceDate(
        minutesOfDay,
        startDate,
        runOverview.startDate.toDate()
      );

      const extractedPowerConsumptionValues = extractDryingProgressGraphDataFromComBoxRun(
        targetSensorKeys,

        humidityYAxis,
        leftYAxis,
        rightYAxis,
        resistanceYAxis,

        offset,
        runOverview,
        showDetails,
        timeScale,

        roomTempEntries,
        roomHumidityEntries,
        roomResistanceEntries,

        floorTempEntries,
        floorHumidityEntries,
        floorResistanceEntries,

        floorKeyTempEntries,
        floorKeyHumidityEntries,
        floorKeyResistanceEntries,

        averagePowerConsumptionEntries,

        weatherTempEntries,
        weatherHumidityEntries,
        weatherPressureEntries,
        weatherPrecipitationEntries,

        showPowerLine,
        weatherData,
        weatherInformationDetailsLevel
      );

      if (
        (
          extractedPowerConsumptionValues?.globalMaximum ||
          extractedPowerConsumptionValues?.globalMaximum === 0
        ) && 
        powerConsumptionValues.globalMaximum < extractedPowerConsumptionValues?.globalMaximum
      ) {
        powerConsumptionValues.globalMaximum = extractedPowerConsumptionValues?.globalMaximum;
      }

      if (
        (
          extractedPowerConsumptionValues?.globalMinimum ||
          extractedPowerConsumptionValues?.globalMinimum === 0
        ) && 
        (
          powerConsumptionValues.globalMinimum > extractedPowerConsumptionValues?.globalMinimum ||
          powerConsumptionValues.globalMinimum === -1
        )
      ) {
        powerConsumptionValues.globalMinimum = extractedPowerConsumptionValues?.globalMinimum;
      }

      if (extractedPowerConsumptionValues?.localMaximum) {
        extractedPowerConsumptionValues.localMaximum.forEach((val, key) => {
          if (powerConsumptionValues.localMaximum[key]) {
            if (powerConsumptionValues.localMaximum[key] < val) {
              powerConsumptionValues.localMaximum[key] = val;
            }
          }
          else {
            powerConsumptionValues.localMaximum[key] = val;
          }
        });
      }
      
      if (extractedPowerConsumptionValues?.localMinimum) {
        extractedPowerConsumptionValues.localMinimum.forEach((val, key) => {
          if (powerConsumptionValues.localMinimum[key]) {
            if (powerConsumptionValues.localMinimum[key] > val) {
              powerConsumptionValues.localMinimum[key] = val;
            }
          }
          else {
            powerConsumptionValues.localMinimum[key] = val;
          }
        });
      }
    });

    const roomTempDataSet = buildComBoxDataSet(
      roomTempEntries,
      getIresDeviceSensorColor(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.TEMPERATURE
      ),
      LEFT_Y_AXIS,
      showDetails,
      ROOM_SENSOR_INDEX,
      undefined,
      undefined,
      'sensor-temp',
      getIresDeviceSensorBaseColor(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.TEMPERATURE
      ),
    );

    const roomHumidityDataSet = buildComBoxDataSet(
      roomHumidityEntries,
      getIresDeviceSensorColor(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.RELATIVE_HUMIDITY
      ),
      RIGHT_Y_AXIS,
      showDetails,
      ROOM_SENSOR_INDEX,
      undefined,
      undefined,
      'sensor-humidity',
      getIresDeviceSensorBaseColor(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.RELATIVE_HUMIDITY
      ),
    );

    const roomResistanceDataSet = buildComBoxDataSet(
      roomResistanceEntries,
      getIresDeviceSensorColor(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.RESISTANCE_DIGITS
      ),
      rightResistanceYAxisId,
      showDetails,
      ROOM_SENSOR_INDEX,
      undefined,
      undefined,
      'sensor-resistance',
      getIresDeviceSensorBaseColor(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.RESISTANCE_DIGITS
      ),
    );

    const avgHumidityDataSet = buildComBoxDataSet(
      floorKeyHumidityEntries,
      getIresDeviceSensorColor(
        IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
        IresDeviceSensorValue.RELATIVE_HUMIDITY
      ),
      RIGHT_Y_AXIS,
      showDetails,
      ROOM_SENSOR_INDEX,
      undefined,
      undefined,
      'sensor-humidity',
      getIresDeviceSensorBaseColor(
        IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
        IresDeviceSensorValue.RELATIVE_HUMIDITY
      ),
    );

    const avgResistanceDataSet = buildComBoxDataSet(
      floorKeyResistanceEntries,
      getIresDeviceSensorColor(
        IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
        IresDeviceSensorValue.RESISTANCE_DIGITS
      ),
      rightResistanceYAxisId,
      showDetails,
      ROOM_SENSOR_INDEX,
      undefined,
      undefined,
      'sensor-resistance',
      getIresDeviceSensorBaseColor(
        IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
        IresDeviceSensorValue.RESISTANCE_DIGITS
      ),
    );

    const avgTempDataSet = buildComBoxDataSet(
      floorKeyTempEntries,
      getIresDeviceSensorColor(
        IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
        IresDeviceSensorValue.TEMPERATURE
      ),
      LEFT_Y_AXIS,
      showDetails,
      ROOM_SENSOR_INDEX,
      undefined,
      undefined,
      'sensor-temp',
      getIresDeviceSensorBaseColor(
        IresDeviceSensor.COM_BOX_GUIDE_SENSOR,
        IresDeviceSensorValue.TEMPERATURE
      ),
    );

    const averagePowerConsumptionDataSet = buildComBoxDataSet(
      averagePowerConsumptionEntries,
      POWER_LINE_COLOR,
      RIGHT_Y_AXIS,
      showDetails,
      ROOM_SENSOR_INDEX,
      true,
      undefined,
      'power',
    );

    const weatherTempDataSet = buildComBoxDataSet(
      weatherTempEntries,
      WEATHER_TEMP_COLOR,
      LEFT_Y_AXIS,
      showDetails,
      WEATHER_INFORMATION_BORDER_WIDTH,
      undefined,
      true,
      'weather-temp',
    );

    const weatherHumidityDataSet = buildComBoxDataSet(
      weatherHumidityEntries,
      WEATHER_HUMIDITY_COLOR,
      RIGHT_Y_AXIS,
      showDetails,
      WEATHER_INFORMATION_BORDER_WIDTH,
      undefined,
      true,
      'weather-humidity',
    );

    const weatherPressureDataSet = buildComBoxDataSet(
      weatherPressureEntries,
      WEATHER_PRESSURE_COLOR,
      RIGHT_Y_AXIS,
      showDetails,
      WEATHER_INFORMATION_BORDER_WIDTH,
      undefined,
      true,
      'weather-pressure',
    );

    const weatherPrecipitationDataSet = buildComBoxDataSet(
      weatherPrecipitationEntries,
      WEATHER_PERCIPITATION_COLOR,
      RIGHT_Y_AXIS,
      showDetails,
      WEATHER_INFORMATION_BORDER_WIDTH,
      undefined,
      true,
      'weather-precipitation',
    );

    if (avgTempDataSet) {
      datasets.push(avgTempDataSet);
    }

    if (avgHumidityDataSet) {
      datasets.push(avgHumidityDataSet);
    }

    if (avgResistanceDataSet) {
      datasets.push(avgResistanceDataSet);
    }

    if (averagePowerConsumptionDataSet && showPowerLine) {
      datasets.push(averagePowerConsumptionDataSet);
    }

    if (weatherData) {
      if (weatherTempDataSet && (!allTempHidden || allSensorValuesHidden)) {
        datasets.push(weatherTempDataSet);
      }
  
      if (weatherHumidityDataSet && (!allHumidityHidden || allSensorValuesHidden)) {
        datasets.push(weatherHumidityDataSet);
      }
      
      if (weatherPressureDataSet) {
        datasets.push(weatherPressureDataSet);
      }

      if (weatherPrecipitationDataSet) {
        weatherPrecipitationDataSet.fill = 'origin';
        weatherPrecipitationDataSet.backgroundColor = COLOR_DRYING_GRAPH_PRECIPITATION_FILL;
        datasets.push(weatherPrecipitationDataSet);
      }
    }

    if (roomTempDataSet) {
      roomTempDataSet.iresSlotLabel = 'R';
      datasets.push(roomTempDataSet);

      sensorValueCombinationCollector.addSensorValueCombination(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.TEMPERATURE
      );
    }

    if (roomHumidityDataSet) {
      roomHumidityDataSet.iresSlotLabel = 'R';
      datasets.push(roomHumidityDataSet);

      sensorValueCombinationCollector.addSensorValueCombination(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.RELATIVE_HUMIDITY
      );
    }

    if (roomResistanceDataSet) {
      roomResistanceDataSet.iresSlotLabel = 'R';
      datasets.push(roomResistanceDataSet);

      sensorValueCombinationCollector.addSensorValueCombination(
        IresDeviceSensor.COM_BOX_ROOM_SENSOR,
        IresDeviceSensorValue.RESISTANCE_DIGITS
      );
    }

    if (showDetails) {
      floorTempEntries.forEach((tempEntrires, sensorIndex) => {
        const sensor = getComBoxSensorForSensorIndex(sensorIndex);
        const colorTemp = getIresDeviceSensorColor(
          sensor,
          IresDeviceSensorValue.TEMPERATURE
        );

        const datasetTemp = buildComBoxDataSet(
          tempEntrires,
          colorTemp,
          LEFT_Y_AXIS,
          showDetails,
          ROOM_SENSOR_INDEX,
          undefined,
          undefined,
          'sensor-temp',
          getIresDeviceSensorBaseColor(
            sensor,
            IresDeviceSensorValue.TEMPERATURE
          ),
        );

        const slotLabel = sensorIndex === ROOM_SENSOR_INDEX
          ? 'R'
          : (formatSensorSlotLabel(
              sensorIndex,
              runGroup[0]?.sensorConfig?.[sensorIndex],
              getSerialForSensorIndex(
                resolvedSerials,
                sensorIndex,
              ),
              isMini,
            ) ?? `${calculateSlotNumberFromIndex(sensorIndex)}L`);

        if (datasetTemp) {
          datasetTemp.iresSlotLabel = slotLabel;
          datasets.push(datasetTemp);

          sensorValueCombinationCollector.addSensorValueCombination(
            sensor,
            IresDeviceSensorValue.TEMPERATURE
          );
        }

        const humidityEntries = floorHumidityEntries[sensorIndex];
        const colorHumidity = getIresDeviceSensorColor(
          sensor,
          IresDeviceSensorValue.RELATIVE_HUMIDITY
        );

        const datasetHumidity = buildComBoxDataSet(
          humidityEntries,
          colorHumidity,
          RIGHT_Y_AXIS,
          showDetails,
          ROOM_SENSOR_INDEX,
          undefined,
          undefined,
          'sensor-humidity',
          getIresDeviceSensorBaseColor(
            sensor,
            IresDeviceSensorValue.RELATIVE_HUMIDITY
          ),
        );

        if (datasetHumidity) {
          datasetHumidity.iresSlotLabel = slotLabel;
          datasets.push(datasetHumidity);

          sensorValueCombinationCollector.addSensorValueCombination(
            sensor,
            IresDeviceSensorValue.RELATIVE_HUMIDITY
          );
        }

        const resistanceEntries = floorResistanceEntries[sensorIndex];
        const colorResistance = getIresDeviceSensorColor(
          sensor,
          IresDeviceSensorValue.RESISTANCE_DIGITS
        );

        const datasetResistance = buildComBoxDataSet(
          resistanceEntries,
          colorResistance,
          rightResistanceYAxisId,
          showDetails,
          ROOM_SENSOR_INDEX,
          undefined,
          undefined,
          'sensor-resistance',
          getIresDeviceSensorBaseColor(
            sensor,
            IresDeviceSensorValue.RESISTANCE_DIGITS
          ),
        );

        if (datasetResistance) {
          datasetResistance.iresSlotLabel = slotLabel;
          datasets.push(datasetResistance);

          sensorValueCombinationCollector.addSensorValueCombination(
            sensor,
            IresDeviceSensorValue.RESISTANCE_DIGITS
          );
        }
      });
    }
  });

  return powerConsumptionValues;
}

export async function extractPanelEntries(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datasets: any[],
  targetSensorKeys: Set<number>,
  runs: RunOverview[],

  spanGaps: boolean,

  timeScale: TimeScale,
  startDate: Date,
  minutesOfDay: number,

  sensorValueCombinationCollector: SensorValueCombinationCollector,

  showDetails: boolean,
  serialNumber: string,

  weatherData?: WeatherData[][],

  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel
) {
  const wallTempEntries: Array<ChartDataEntry> = [];

  const heatRatioEntries: Array<ChartDataEntry> = [];
  // Ungated intensity series driving the temperature-band fill tint (all users).
  const fillIntensityEntries: Array<ChartDataEntry> = [];
  const wallTempMaxStepEntries: Array<ChartDataEntry> = [];

  const groundTempEntries: Array<ChartDataEntry> = [];
  const groundTempMaxStepEntries: Array<ChartDataEntry> = [];

  const weatherTempEntries: Array<ChartDataEntry> = [];
  const weatherHumidityEntries: Array<ChartDataEntry> = [];
  const weatherPressureEntries: Array<ChartDataEntry> = [];
  const weatherPrecipitationEntries: Array<ChartDataEntry> = [];

  for (const [index, runOverview] of runs.entries()) {
    const offset = calculateOffsetToReferenceDate(
      minutesOfDay,
      startDate,
      runOverview.startDate.toDate()
    );

    const previousRun = runs[index - 1];
    const addBreak =
      previousRun && previousRun.chainNumber != runOverview.chainNumber;

    await extractDryingProgressGraphDataFromPanelRun(
      targetSensorKeys,
      offset,
      runOverview,
      addBreak,
      wallTempEntries,
      heatRatioEntries,
      fillIntensityEntries,
      wallTempMaxStepEntries,
      groundTempEntries,
      groundTempMaxStepEntries,
      timeScale,
      serialNumber,

      weatherTempEntries,
      weatherHumidityEntries,
      weatherPressureEntries,
      weatherPrecipitationEntries,

      weatherData,
      weatherInformationDetailsLevel
    );
  }

  const heatRatioDataSet = buildHeatRatioDataSet(heatRatioEntries, false);

  if (heatRatioDataSet) {
    datasets.push(heatRatioDataSet);

    sensorValueCombinationCollector.addSensorValueCombination(
      IresDeviceSensor.PANEL_WALL,
      IresDeviceSensorValue.INTENSITY
    );
  }

  const wallTempDataSet = buildTemperatureDataSet(
    true,
    spanGaps,
    showDetails,
    wallTempEntries,
    wallTempMaxStepEntries,
    fillIntensityEntries
  );

  if (wallTempDataSet) {
    datasets.push(wallTempDataSet.max);
    datasets.push(wallTempDataSet.min);

    sensorValueCombinationCollector.addSensorValueCombination(
      IresDeviceSensor.PANEL_WALL,
      IresDeviceSensorValue.TEMPERATURE
    );
  }

  const groundTempDataSet = buildTemperatureDataSet(
    false,
    spanGaps,
    showDetails,
    groundTempEntries,
    groundTempMaxStepEntries,
    fillIntensityEntries
  );

  if (groundTempDataSet) {
    datasets.push(groundTempDataSet.max);
    datasets.push(groundTempDataSet.min);

    sensorValueCombinationCollector.addSensorValueCombination(
      IresDeviceSensor.PANEL_GROUND,
      IresDeviceSensorValue.TEMPERATURE
    );
  }

  const weatherTempDataSet = buildWeatherDataSet(
    weatherTempEntries,
    WEATHER_TEMP_COLOR,
    showDetails,
    true,
    WEATHER_INFORMATION_BORDER_WIDTH,
    'weather-temp',
  );

  const weatherHumidityDataSet = buildWeatherDataSet(
    weatherHumidityEntries,
    WEATHER_HUMIDITY_COLOR,
    showDetails,
    false,
    WEATHER_INFORMATION_BORDER_WIDTH,
    'weather-humidity',
  );

  const weatherPressureDataSet = buildWeatherDataSet(
    weatherPressureEntries,
    WEATHER_PRESSURE_COLOR,
    showDetails,
    false,
    WEATHER_INFORMATION_BORDER_WIDTH,
    'weather-pressure',
  );

  const weatherPrecipitationDataSet = buildWeatherDataSet(
    weatherPrecipitationEntries,
    WEATHER_PERCIPITATION_COLOR,
    showDetails,
    false,
    WEATHER_INFORMATION_BORDER_WIDTH,
    'weather-precipitation',
  );

  // Hide weather temperature when all panel temperature sensors are toggled off.
  const allPanelTempHidden = !Array.from(targetSensorKeys).some(
    (key) => iresSensorAndValueFromKey(key).value === IresDeviceSensorValue.TEMPERATURE
  );

  if (weatherData) {
    if (weatherTempDataSet && !allPanelTempHidden) {
      datasets.push(weatherTempDataSet);
    }

    if (weatherHumidityDataSet) {
      datasets.push(weatherHumidityDataSet);
    }
    
    if (weatherPressureDataSet) {
      datasets.push(weatherPressureDataSet);
    }

    if (weatherPrecipitationDataSet) {
      weatherPrecipitationDataSet.fill = 'origin';
      weatherPrecipitationDataSet.backgroundColor = COLOR_DRYING_GRAPH_PRECIPITATION_FILL;
      datasets.push(weatherPrecipitationDataSet);
    }
  }
}

async function adjustEndDate(
  serialNumber: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  run: any
) {
  const isPanel = getIresDeviceCategoryFromSerialNumber(serialNumber) === IresDeviceCategory.PANEL;
  const samples = await loadSamplesOfRun(serialNumber, run.id);
  const iresDeviceResponseReader = new IresDeviceResponseReader();

  const date = run.startDate.toDate();
  let adjustedEndDate = null;

  let previousTimeDiffInMin = 0;
  let timeDiffToBeAdjusted = 0;
  
  samples.forEach((rawSampleData) => {
    const data = isPanel
      ? hexToUint8Array(rawSampleData.encodedSample)
      : new Uint8Array(
          base64Buffer.decode(rawSampleData.encodedSample)
        );
    iresDeviceResponseReader.setNewData(data);

    if (!isPanel) {
      iresDeviceResponseReader.readUint16();
      iresDeviceResponseReader.readUint8();
      iresDeviceResponseReader.readUint8();

      if (hasV2OrGreaterSampleData(run)) {
        iresDeviceResponseReader.readUint8();
        iresDeviceResponseReader.readUint8();
        iresDeviceResponseReader.readUint8();
      }
    }

    const timeDiffInMinutes_ = isPanel
      ? getPanelUnit16Value(
        iresDeviceResponseReader
      )
      : iresDeviceResponseReader.readUint16();

    if (rawSampleData.sampleIndex === 0) {
      timeDiffToBeAdjusted = timeDiffInMinutes_;
    }
    
    const timeDiffInMinutes = timeDiffInMinutes_ - timeDiffToBeAdjusted;

    advanceByMinutes(date, timeDiffInMinutes - previousTimeDiffInMin);

    if (rawSampleData.sampleIndex === run.lastLoadedSampleIndex) {
      adjustedEndDate = date;
    }

    previousTimeDiffInMin = timeDiffInMinutes;
  });

  return adjustedEndDate ?? run.endDate.toDate();
}

export async function createLimitLines(
  iresDeviceCategory: IresDeviceCategory,
  runs: RunOverview[],
  startDate: Date,
  minutesOfDay: number,
  hideRunStartsCausedBeMaxRunDuration: boolean,
  serialNumber: string,
  showLimitLinesForRuns?: boolean,
) {
  const limitLines: LimitLine[] = [];

  const runStartColor = COLOR_DRYING_GRAPH_RUN_START;
  const runEndColor = COLOR_DRYING_GRAPH_RUN_END;

  runs.forEach(async (run, index) => {
    const runStartX = calculateOffsetToReferenceDate(
      minutesOfDay,
      startDate,
      run.startDate.toDate()
    );

    let addStartOfRunLimitLine = true;

    if (
      hideRunStartsCausedBeMaxRunDuration &&
      iresDeviceCategory === IresDeviceCategory.COMBOX
    ) {
      const firstSample = run.samples[0];

      if (firstSample) {
        //Don't show limit line when run was started because last run was ended because of reaching max run duration
        addStartOfRunLimitLine = !isRunReachedMaxDurationSample(
          firstSample as ComBoxSampleInfo
        );
      }
    }

    if (addStartOfRunLimitLine && showLimitLinesForRuns) {
      limitLines.push({
        xValue: runStartX,
        color: runStartColor,
      });
    }

    const nextRun = runs[index + 1];

    if (nextRun) {
      /* Run separators are IRES-only (VIEW_LIMIT_LINES_FOR_RUNS), the end line as much as the start
         line. Skipping early also spares a client the adjustEndDate lookup below. */
      if (showLimitLinesForRuns && wasRunInterrupted(nextRun, runs.slice(0, index + 1))) {
        // An earlier run can have ended after this one (see getLatestRunEndMillis): the line goes
        // where the power cut starts, at the later end.
        const runEndDate = new Date(
          Math.max(
            (hasPlaceholderClockDate(run.endDate)
              ? await adjustEndDate(serialNumber, run)
              : run.endDate.toDate()
            ).getTime(),
            getLatestRunEndMillis(runs.slice(0, index))
          )
        );

        limitLines.push({
          xValue: getDrawnRunEndX(
            minutesOfDay,
            startDate,
            runEndDate,
            getLastSampleDate(run)
          ),
          color: runEndColor,
        });
      }
    }
  });

  return {
    limitLines: limitLines,
  };
}

function getDatesForExtractingWeatherInformation(startDate: Date, endDate: Date) {
  const start = startDate;
  const end = endDate;

  const dates: string[] = [];

  while (start <= end) {
    dates.push(start.toISOString().split('T')[0]);
    start.setDate(start.getDate() + 1);
  }

  return dates;
}

let weatherDataCache: {
  key: string;
  data: WeatherData[][];
} | null = null;

function getWeatherCacheKey(
  startDate: Date,
  endDate: Date,
  latitude: number,
  longitude: number,
) {
  return `${latitude.toFixed(4)}_${longitude.toFixed(4)}_${startDate.getTime()}_${endDate.getTime()}`;
}

export function clearWeatherDataCache() {
  weatherDataCache = null;
}

/**
 * Pre-fetches weather data and populates the module-level cache.
 * Call early (e.g. on page load) so the data is ready when the user enables
 * the weather overlay. Silently ignores errors — weather is best-effort.
 */
export async function prefetchWeatherData(
  startDate: Date,
  endDate: Date,
  latitude: number,
  longitude: number,
): Promise<void> {
  const cacheKey = getWeatherCacheKey(startDate, endDate, latitude, longitude);
  if (weatherDataCache && weatherDataCache.key === cacheKey) {
    return; // already cached
  }

  const datesForExtractingWeatherInformation =
    getDatesForExtractingWeatherInformation(new Date(startDate), endDate);

  const data = await fetchWeatherInformation(
    latitude,
    longitude,
    datesForExtractingWeatherInformation
  );

  if (data !== null) {
    weatherDataCache = { key: cacheKey, data };
  }
}

async function getWeatherData(
  startDate: Date,
  endDate: Date,
  latitude?: number,
  longitude?: number,
) {
  if (!latitude || !longitude) {
    return undefined;
  }

  const cacheKey = getWeatherCacheKey(startDate, endDate, latitude, longitude);
  if (weatherDataCache && weatherDataCache.key === cacheKey) {
    return weatherDataCache.data;
  }

  const datesForExtractingWeatherInformation = 
    getDatesForExtractingWeatherInformation(new Date(startDate), endDate);
  
  const data = await fetchWeatherInformation(
    latitude,
    longitude,
    datesForExtractingWeatherInformation
  );

  if (data === null) {
    return null;
  }

  weatherDataCache = { key: cacheKey, data };
  return data;
}

/**
 * Centres a phase label (P2, P3, …) on its 20 px band. The annotation plugin keeps a label's padded
 * box inside the plot, and its default 6 px padding makes that box 26 px tall: with the band near the
 * bottom of the plot the label was pushed up out of the band, further the shorter the graph. The
 * label background is transparent, so the padding drew nothing.
 */
const PHASE_LABEL_POSITION = { yAdjust: 0, padding: 0 };

const ESTIMATED_CHAR_WIDTH_PX = 4;
const LABEL_PADDING_PX = 2;
const MIN_CHART_WIDTH_PX = 300;

function doesLabelFitInRegion(
  label: string,
  regionStartScaled: number,
  regionEndScaled: number,
  chartXMin: number,
  chartXMax: number,
) {
  const chartRange = chartXMax - chartXMin;
  if (chartRange <= 0) return false;

  const regionWidth = regionEndScaled - regionStartScaled;
  const regionFraction = regionWidth / chartRange;

  const estimatedLabelWidthPx = label.length * ESTIMATED_CHAR_WIDTH_PX + LABEL_PADDING_PX;
  const minFraction = estimatedLabelWidthPx / MIN_CHART_WIDTH_PX;

  return regionFraction >= minFraction;
}

function getFormattedPhaseLabelsType1(vue: Vue, phase: number) {
  switch (phase) {
    case 1:
      return vue.$t("rateDrying.formatPhases.boost").toString();
    case 2:
      return vue.$t("rateDrying.formatPhases.smart").toString();
    case 3:
      return vue.$t("rateDrying.formatPhases.comfort").toString();
    case 4:
      return vue.$t("rateDrying.formatPhases.eco").toString();

    default:
      return "";
  }
}

export async function extractDryingProgressGraphData(
  vue: Vue,
  iresDevice: IresDevice,
  runs: Array<RunOverview>,
  targetSensors: IresSensorAndValueCombination[],
  dryingProgressGraphOptions: DryingProgressGraphOptions,
  statusHistory: StatusHistory[],
  showPowerLine?: boolean,
  latitude?: number,
  longitude?: number,
  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel,
  showRunStartLimitLines?: boolean,
  isActiveChain?: boolean,
): Promise<DryingProgressChartData | null> {
  if (!iresDevice) {
    return null;
  }

  // Chart.js does not follow the Vuetify theme, so dark-mode colors are chosen
  // explicitly. Read LIVE (not captured once) so the theme-aware chart colors
  // below can be used as scriptable options: on a theme toggle a cheap
  // chart.update() re-themes the whole chart with no re-extraction/refetch.
  // Used for axis text/grid, region fills and annotation labels below.
  const isDark = () => !!vue?.$vuetify?.theme?.dark;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const datasets: any[] = [];

  const targetSensorKeys = new Set<number>();

  targetSensors.forEach((value) => {
    targetSensorKeys.add(iresSensorAndValueToKey(value));
  });

  const iresDeviceCategory = getIresDeviceCategory(iresDevice);
  const isPanel = iresDeviceCategory === IresDeviceCategory.PANEL;

  const humidityUnit = getComBoxHumidityUnit();

  const leftYAxis = dryingProgressGraphOptions.leftYAxis
    ? dryingProgressGraphOptions.leftYAxis
    : isPanel
      ? DryingGraphYAxis.PANEL_TEMPERATURE
      : DryingGraphYAxis.COM_BOX_TEMPERATURE;

  const humidityYAxis = getMatchingDryingGraphYAxis(humidityUnit);

  let rightYAxis = dryingProgressGraphOptions.rightYAxis
    ? dryingProgressGraphOptions.rightYAxis
    : isPanel
      ? DryingGraphYAxis.PANEL_INTENSITY
      : humidityYAxis;

  const hasResistanceSelection = Array.from(targetSensorKeys).some(
    (key) => iresSensorAndValueFromKey(key).value === IresDeviceSensorValue.RESISTANCE_DIGITS
  );
  const hasHumiditySelection = Array.from(targetSensorKeys).some(
    (key) => iresSensorAndValueFromKey(key).value === IresDeviceSensorValue.RELATIVE_HUMIDITY
  );

  let useSeparateResistanceYAxis = false;

  if (!isPanel) {
    // Resolve right-axis behavior from active selections only.
    // This avoids stale persisted axis values and prevents duplicate scales.
    if (hasHumiditySelection && hasResistanceSelection) {
      rightYAxis = humidityYAxis;
      useSeparateResistanceYAxis = false;
    } else if (hasHumiditySelection) {
      rightYAxis = humidityYAxis;
    } else if (hasResistanceSelection) {
      rightYAxis = DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS;
    } else {
      // Fallback to humidity axis when no right-axis value type is selected.
      rightYAxis = humidityYAxis;
    }
  }

  const resistanceYAxis = useSeparateResistanceYAxis
    ? DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
    : rightYAxis;
  const rightResistanceYAxisId = useSeparateResistanceYAxis
    ? RIGHT_Y_AXIS_RESISTANCE
    : RIGHT_Y_AXIS;

  const lastRun = runs[runs.length - 1];
  const startDate = runs[0]?.startDate?.toDate() ?? new Date();

  let endDate = lastRun ? getAdjustedEndDate(lastRun) : new Date();

  if (dryingProgressGraphOptions.forcedEndDate) {
    endDate = dryingProgressGraphOptions.forcedEndDate;
  }

  if (hasPlaceholderClockDate(endDate)) {
    endDate = await adjustEndDate(iresDevice.serialNo, lastRun);
  }

  startDate.setSeconds(0);
  startDate.setMilliseconds(0);

  endDate.setSeconds(0);
  endDate.setMilliseconds(0);

  const minutesOfDay = startDate.getHours() * 60 + startDate.getMinutes();
  const durationInMinutes = Math.max(
    0,
    Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60))
  );

  const timeScale = new TimeScale(
    DateTime.fromJSDate(startDate),
    DateTime.fromJSDate(endDate),
    durationInMinutes
  );

  const weatherData = await getWeatherData(startDate, endDate, latitude, longitude);
  const weatherDataLoadFailed = latitude !== undefined && longitude !== undefined
    && weatherInformationDetailsLevel !== undefined
    && weatherInformationDetailsLevel !== WeatherInformationDetailsLevel.NONE
    && weatherData === null;
  
  const showDetails = dryingProgressGraphOptions.showDetails ?? false;
  const sensorValueCombinationCollector = new SensorValueCombinationCollector();

  let powerConsumptionValues: PowerConsumptionValues | undefined = initializePowerConsumptionValues();

  //We only want to gaps in heat ratio values if all runs belong to the same program chain
  const spanGaps =
    _.chain(runs)
      .map((run) => run.chainNumber)
      .uniq()
      .value().length <= 1;

  if (isPanel) {
    await extractPanelEntries(
      datasets,
      targetSensorKeys,

      runs,
      spanGaps,

      timeScale,
      startDate,
      minutesOfDay,

      sensorValueCombinationCollector,

      showDetails,
      iresDevice.serialNo,

      weatherData ?? undefined,
      weatherInformationDetailsLevel
    );
  } else {
    powerConsumptionValues = extractComBoxEntries(
      datasets,
      targetSensorKeys,

      humidityYAxis,
      leftYAxis,
      rightYAxis,
      rightResistanceYAxisId,
      resistanceYAxis,

      runs,
      showDetails,

      timeScale,
      startDate,
      minutesOfDay,

      sensorValueCombinationCollector,

      showPowerLine,

      weatherData ?? undefined,
      weatherInformationDetailsLevel,

      isHyDryMiniSerial(iresDevice.serialNo),

      isActiveChain ? (iresDevice as ComBox).comBoxStatus?.sensorSlotSerials : undefined,
    );
  }

  const chartData = {
    datasets: datasets,
  };

  let maxTicksLimit: number | undefined = 8;

  if (timeScale.timeScaleUnit === TimeScaleUnit.DAYS) {
    if (getNumberDaysBetween(startDate, endDate) > 6) {
      maxTicksLimit = 12;
    } else {
      maxTicksLimit = Math.min(
        maxTicksLimit,
        Math.ceil(timeScale.scaleToMinutes(durationInMinutes))
      );
    }
  } else {
    if (durationInMinutes < 10) {
      maxTicksLimit = 2;
    } else if (durationInMinutes < 60) {
      maxTicksLimit = 4;
    } else if (durationInMinutes < 120) {
      maxTicksLimit = 6;
    }
  }

  const offsetStart = DateTime.fromJSDate(startDate).offset;
  const offsetEnd = DateTime.fromJSDate(endDate).offset;

  const timeDiffInMinutes =
    offsetEnd -
    offsetStart +
    (endDate.getTime() - startDate.getTime()) / (1000 * 60);

  const xMin = timeScale.scaleMinutes(minutesOfDay);
  const xMax = timeScale.scaleMinutes(
    minutesOfDay + Math.max(1, timeDiffInMinutes),
    true
  );

  const hideLimitLines = dryingProgressGraphOptions.hideLimitLines ?? false;

  const leftYAxisMinMax = getYAxisMinMax(leftYAxis);
  const rightYAxisMinMax = getYAxisMinMax(rightYAxis);
  const resistanceYAxisMinMax = getYAxisMinMax(resistanceYAxis);

  const { limitLines } = await createLimitLines(
    iresDeviceCategory,
    runs,
    startDate,
    minutesOfDay,
    dryingProgressGraphOptions.hideRunStartsCausedBeMaxRunDuration ?? true,
    iresDevice.serialNo,
    showRunStartLimitLines
  );

  const limitLinesAnnotations =
    hideLimitLines || !showDetails
      ? []
      : limitLines.map((limitLine) => {
          return {
            drawTime: "afterDatasetsDraw",
            type: "line",
            mode: "vertical",
            scaleID: X_AXIS_ID,
            xScaleID: X_AXIS_ID,
            yScaleID: LEFT_Y_AXIS,
            borderColor: limitLine.color,
            borderWidth: 2,
            value: timeScale.scaleMinutes(limitLine.xValue),
          };
        });

  const timeRegionsOfInterest = await findTimeRegionsOfInterestInRuns(
    iresDevice.serialNo,
    runs
  );

  const timeRegionsOfInterestOfPanel = await findTimeRegionsOfInterestInRunsOfPanel(runs, iresDevice.serialNo);
  
  if (isPanel) {
    const panel = iresDevice as Panel;
    const errorOrWarningTimeIntervalsOfPanel = calculateErrorOrWarningTimeIntervalsOfPanel(statusHistory, panel.panelStatus);

    errorOrWarningTimeIntervalsOfPanel.forEach((statusHistoryItem) => {
      const indicesToBeRemoved: number[] = [];
      timeRegionsOfInterestOfPanel.forEach((timeRegion, index) => {
        if (timeRegion.startDate >= statusHistoryItem.startDate && timeRegion.startDate <= statusHistoryItem.endDate) {
          if (timeRegion.endDate <= statusHistoryItem.endDate) {
            indicesToBeRemoved.push(index);
          }
          else {
            timeRegion.startDate = statusHistoryItem.endDate;
          }
        }
        else if (statusHistoryItem.startDate >= timeRegion.startDate && statusHistoryItem.startDate <= timeRegion.endDate) {
          if (statusHistoryItem.endDate <= timeRegion.endDate) {
            timeRegionsOfInterestOfPanel.push({
              startDate: statusHistoryItem.endDate,
              endDate: timeRegion.endDate,
              regionOfInterestType: timeRegion.regionOfInterestType,
            });
          }
          
          timeRegion.endDate = statusHistoryItem.startDate;
        }
        else if (timeRegion.endDate >= statusHistoryItem.startDate && timeRegion.endDate <= statusHistoryItem.endDate) {
          timeRegion.endDate = statusHistoryItem.startDate;
        }
      });

      indicesToBeRemoved
        .reverse()
        .forEach((index) => {
          timeRegionsOfInterestOfPanel.splice(index, 1);
        })
      
      timeRegionsOfInterestOfPanel.push(statusHistoryItem);
    });
  }

  const phaseAnnotations =
    showDetails && !dryingProgressGraphOptions.hidePhaseAnnotations
      ? isPanel 
        ? timeRegionsOfInterestOfPanel
          .map((region) => {
            const regionStartDate = calculateOffsetToReferenceDate(
              minutesOfDay,
              startDate,
              region.startDate
            );

            const regionEndDate = calculateOffsetToReferenceDate(
              minutesOfDay,
              startDate,
              region.endDate
            );

            const yAxisDelta = leftYAxisMinMax.max - leftYAxisMinMax.min;
            const yPosition = leftYAxisMinMax.min + yAxisDelta * 0.05;

            let label = "";
            let backgroundColor = "transparent";

            let shouldLabelBeDisplayed = true;

            if (region.startDate < endDate) {
              if (region.regionOfInterestType ===
                ProgramChainTimeRegionOfInterestTypeOfPanel.HAS_ERROR
              ) {
                label = vue
                  .$t("panelDetail.dryingPhaseInfo.error")
                  .toString();
                shouldLabelBeDisplayed = doesLabelFitInRegion(
                  label,
                  timeScale.scaleMinutes(regionStartDate),
                  timeScale.scaleMinutes(regionEndDate),
                  xMin, xMax
                );
                backgroundColor = COLOR_GRAPH_LIGHT_RED;
              }
              else if (region.regionOfInterestType ===
                ProgramChainTimeRegionOfInterestTypeOfPanel.HAS_WARNING
              ) {
                label = vue
                  .$t("panelDetail.dryingPhaseInfo.warning")
                  .toString();
                shouldLabelBeDisplayed = doesLabelFitInRegion(
                  label,
                  timeScale.scaleMinutes(regionStartDate),
                  timeScale.scaleMinutes(regionEndDate),
                  xMin, xMax
                );
                backgroundColor = COLOR_DRYING_GRAPH_PHASE_INFO_WARNING;
              }
              else if (
                region.regionOfInterestType ===
                ProgramChainTimeRegionOfInterestTypeOfPanel.PROGRAM_0
              ) {
                label = vue
                  .$t("panelDetail.dryingPhaseInfo.program0")
                  .toString();
                shouldLabelBeDisplayed = doesLabelFitInRegion(
                  label,
                  timeScale.scaleMinutes(regionStartDate),
                  timeScale.scaleMinutes(regionEndDate),
                  xMin, xMax
                );
                backgroundColor = COLOR_DRYING_GRAPH_PHASE_INFO_POST_DRYING;
              } else if (
                region.regionOfInterestType ===
                ProgramChainTimeRegionOfInterestTypeOfPanel.PROGRAM_1_TO_7
              ) {
                label = vue
                  .$t("panelDetail.dryingPhaseInfo.program1ToProgram7")
                  .toString();
                shouldLabelBeDisplayed = doesLabelFitInRegion(
                  label,
                  timeScale.scaleMinutes(regionStartDate),
                  timeScale.scaleMinutes(regionEndDate),
                  xMin, xMax
                );
                backgroundColor = PHASE_ANNOTATION_COLORS[2];
              }
            }
            
            const annotation: LineAnnotationOptions = {
              xScaleID: X_AXIS_ID,
              yScaleID: LEFT_Y_AXIS,
              yMin: yPosition,
              yMax: yPosition,
              borderColor: backgroundColor,
              borderWidth: 20,
              xMin: timeScale.scaleMinutes(regionStartDate),
              xMax: timeScale.scaleMinutes(regionEndDate),
              label: {
                ...PHASE_LABEL_POSITION,
                enabled: shouldLabelBeDisplayed,
                backgroundColor: COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
                color: "black",
                content: label,
              },
            };

            return {
              ...annotation,
              type: "line",
            };
          })
          .reverse()
        : timeRegionsOfInterest
          .filter(
            (region) =>
              region.regionOfInterestType ===
                ProgramChainTimeRegionOfInterestType.PHASE ||
              region.regionOfInterestType ===
                ProgramChainTimeRegionOfInterestType.POST_DRYING_RUN
          )
          .map((region) => {
            const regionStartDate = calculateOffsetToReferenceDate(
              minutesOfDay,
              startDate,
              region.startDate
            );

            const regionEndDate = calculateOffsetToReferenceDate(
              minutesOfDay,
              startDate,
              region.endDate
            );

            const yAxisDelta = leftYAxisMinMax.max - leftYAxisMinMax.min;
            const yPosition = leftYAxisMinMax.min + yAxisDelta * 0.05;

            let label = "";
            let backgroundColor = "transparent";

            let shouldLabelBeDisplayed = true;

            if (
              region.regionOfInterestType ===
              ProgramChainTimeRegionOfInterestType.PHASE
            ) {
              const info = region.data as DryingPhaseInfo;

              label = info.formatPhasesType1
                ? getFormattedPhaseLabelsType1(vue, info.phase)
                : vue
                  .$t("rateDrying.formatPhaseShort", [info.phase])
                  .toString();

              const phaseAnnotationColorIndex =
                (info.phase - 1) % PHASE_ANNOTATION_COLORS.length;

              backgroundColor =
                PHASE_ANNOTATION_COLORS[phaseAnnotationColorIndex];

              if (info.isOpenEndedPhase) {
                shouldLabelBeDisplayed = true;
              } else {
                shouldLabelBeDisplayed = doesLabelFitInRegion(
                  label,
                  timeScale.scaleMinutes(regionStartDate),
                  timeScale.scaleMinutes(regionEndDate),
                  xMin, xMax
                );
              }
            } else if (
              region.regionOfInterestType ===
              ProgramChainTimeRegionOfInterestType.POST_DRYING_RUN
            ) {
              backgroundColor = COLOR_DRYING_GRAPH_PHASE_INFO_POST_DRYING;

              if (region.data) {
                const info = region.data as DryingPhaseInfo;
                
                if (info.formatPhasesType1) {
                  label = getFormattedPhaseLabelsType1(vue, info.phase);

                  if (info.isOpenEndedPhase) {
                    shouldLabelBeDisplayed = true;
                  } else {
                    shouldLabelBeDisplayed = doesLabelFitInRegion(
                      label,
                      timeScale.scaleMinutes(regionStartDate),
                      timeScale.scaleMinutes(regionEndDate),
                      xMin, xMax
                    );
                  }
                }
              }
            }

            const annotation: LineAnnotationOptions = {
              xScaleID: X_AXIS_ID,
              yScaleID: LEFT_Y_AXIS,
              yMin: yPosition,
              yMax: yPosition,
              borderColor: backgroundColor,
              borderWidth: 20,
              xMin: timeScale.scaleMinutes(regionStartDate),
              xMax: timeScale.scaleMinutes(regionEndDate),
              label: {
                ...PHASE_LABEL_POSITION,
                enabled: shouldLabelBeDisplayed,
                backgroundColor: COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
                color: "black",
                content: label,
              },
            };

            return {
              ...annotation,
              type: "line",
            };
          })
          .reverse()
      : [];

  const regionsOfInterestAnnotations = timeRegionsOfInterest
    .filter(
      (region) =>
        region.regionOfInterestType ===
          ProgramChainTimeRegionOfInterestType.POWER_FAIL ||
        region.regionOfInterestType ===
          ProgramChainTimeRegionOfInterestType.DRYING_PAUSE
    )
    .map((region) => {
      // A power cut's band starts where its run end line is drawn, at the last reading.
      const regionStartDate = getDrawnRunEndX(
        minutesOfDay,
        startDate,
        region.startDate,
        region.lastSampleDate
      );

      const regionEndDate = calculateOffsetToReferenceDate(
        minutesOfDay,
        startDate,
        region.endDate
      );

      const isDryingPause =
        region.regionOfInterestType ===
        ProgramChainTimeRegionOfInterestType.DRYING_PAUSE;

      // Scriptable (reads the live theme) so a toggle re-themes the band on the
      // next chart.update without re-extraction.
      const regionFill = () =>
        isDryingPause
          ? isDark()
            ? COLOR_DRYING_GRAPH_REGION_DRYING_PAUSE_DARK
            : COLOR_DRYING_GRAPH_REGION_DRYING_PAUSE
          : isDark()
          ? COLOR_DRYING_GRAPH_REGION_POWER_FAIL_DARK
          : COLOR_DRYING_GRAPH_REGION_POWER_FAIL;

      return {
        drawTime: "beforeDatasetsDraw",
        type: "box",
        xScaleID: X_AXIS_ID,
        yScaleID: RIGHT_Y_AXIS,
        // Left edge of the box. in units along the x axis
        xMin: timeScale.scaleMinutes(regionStartDate),

        // Right edge of the box
        xMax: timeScale.scaleMinutes(regionEndDate),

        // Top edge of the box in units along the y axis
        yMax: 0,

        // Bottom edge of the box
        yMin: 105,
        backgroundColor: regionFill as unknown as string,
        borderWidth: 0,
        borderColor: COLOR_DRYING_GRAPH_TRANSPARENT_BLACK,
      };
    });

  const annotationsObject = {
    ...Object.assign(
      {},
      ...regionsOfInterestAnnotations.map((annotation, index) => ({
        [`coloredArea_${index}`]: annotation,
      }))
    ),
    ...Object.assign(
      {},
      ...limitLinesAnnotations.map((annotation, index) => ({
        [`limitLines_${index}`]: annotation,
      }))
    ),
    ...Object.assign(
      {},
      ...phaseAnnotations.map((annotation, index) => ({
        [`${PHASE_ANNOTATION_KEY_PREFIX}${index}`]: annotation,
      }))
    ),
  };

  // Theme-aware axis colors, scriptable (read the live theme) so a toggle
  // re-themes them on the next chart.update without re-extraction.
  const axisTickColor = () => (isDark() ? "#d0d0d0" : "black");
  const axisGridColor = () =>
    isDark() ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.1)";

  const options: IresChartOptions = {
    hover: { mode: undefined },
    iresInfo: {
      id: runs[0]?.id,
      allowZoom: showDetails,
      xMin: xMin,
      xMax: xMax,
      durationInMinutes: durationInMinutes,
      rightYAxis: rightYAxis,
      startDate: startDate,
      timeScale: timeScale,
    },
    animation: {
      duration: 0,
    },
    scales: {
      [X_AXIS_ID]: {
        display: showDetails,
        axis: "x",
        position: "bottom",
        min: xMin,
        max: xMax,
        grid: { color: axisGridColor },
        ticks: {
          ...(showDetails
            ? {
                maxTicksLimit: maxTicksLimit,
                padding: 10,
                minRotation: 0,
                maxRotation: 0,
                autoSkip: true,
                font: {
                  size: 18,
                },
                color: axisTickColor,
                callback: (
                  tickValue: number | string,
                  index: number,
                  ticks: Tick[]
                ) => {
                  if (runs.length === 0) {
                    return "";
                  }

                  return (
                    getXAxisLabel(
                      Number(tickValue),
                      index,
                      ticks,
                      startDate,
                      durationInMinutes,
                      timeScale,
                      xMin,
                      xMax
                    ) ?? ""
                  );
                },
              }
            : {}),
        },
      },
      [LEFT_Y_AXIS]: {
        axis: "y",
        display: showDetails,
        position: "left",
        min: leftYAxisMinMax.min,
        max: leftYAxisMinMax.max,
        grid: { color: axisGridColor },
        ticks: {
          color: COLOR_GRAPH_DARK_RED,
          font: {
            size: 18,
          },
          stepSize: leftYAxisMinMax.stepSize,
          ...(showDetails
            ? {
                callback: (value: number | string) => {
                  const result = formatYAxisValue(
                      vue,
                      leftYAxis,
                      Number(value),
                      leftYAxisMinMax.max,
                      viewWeatherInformationAccess(getEffectiveUserLazily()),
                      undefined,
                      weatherInformationDetailsLevel
                    );
                  if (Array.isArray(result)) {
                    return result.map(line => " " + line);
                  }
                  return " " + result;
                },
              }
            : {}),
        },
      },
      [RIGHT_Y_AXIS]: {
        display: showDetails,
        axis: "y",
        position: "right",
        min: rightYAxisMinMax.min,
        max: rightYAxisMinMax.max,
        grid: { color: axisGridColor },
        ticks: {
          stepSize: rightYAxisMinMax.stepSize,
          font: {
            size: 18,
          },
          color: isPanel ? COLOR_DRYING_GRAPH_INTENSITY : COLOR_GRAPH_DARK_BLUE,
          ...(showDetails
            ? {
                callback: (value: number | string) => {
                  const result = formatYAxisValue(
                    vue,
                    rightYAxis,
                    Number(value),
                    rightYAxisMinMax.max,
                    viewPowerInformationAccess(getEffectiveUserLazily()),
                    showPowerLine,
                    weatherInformationDetailsLevel,
                    hasResistanceSelection && !useSeparateResistanceYAxis
                  );

                  if (Array.isArray(result)) {
                    return result.map((line) => " " + line);
                  }

                  return " " + result;
                },
              }
            : {}),
        },
      },
      ...(useSeparateResistanceYAxis
        ? {
            [RIGHT_Y_AXIS_RESISTANCE]: {
              display: showDetails,
              axis: "y",
              position: "right",
              offset: true,
              grid: {
                drawOnChartArea: false,
              },
              min: resistanceYAxisMinMax.min,
              max: resistanceYAxisMinMax.max,
              ticks: {
                stepSize: resistanceYAxisMinMax.stepSize,
                font: {
                  size: 18,
                },
                color: COLOR_DRYING_GRAPH_RESISTANCE_FLOOR_SENSOR_1,
                ...(showDetails
                  ? {
                      callback: (value: number | string) => {
                        const result = formatYAxisValue(
                          vue,
                          DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS,
                          Number(value),
                          resistanceYAxisMinMax.max,
                          viewPowerInformationAccess(getEffectiveUserLazily()),
                          showPowerLine,
                          weatherInformationDetailsLevel,
                          false
                        );

                        if (Array.isArray(result)) {
                          return result.map((line) => " " + line);
                        }

                        return " " + result;
                      },
                    }
                  : {}),
              },
            },
          }
        : {}),
    },
    maintainAspectRatio: false,
    layout: {
      padding: 0,
    },
    parsing: false,
    normalized: true,
    plugins: {
      decimation: {
        enabled: !showDetails,
        algorithm: "min-max",
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      annotation: {
        annotations: {
          ...annotationsObject,
        },
      },
    },
  };

  const metaData: DryingProgressChartDataMetaData = {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    durationInMinutes: durationInMinutes,

    xAxisMinValue: xMin,
    xAxisMaxValue: xMax,
    maxTicksLimit: maxTicksLimit,

    leftYAxisMinValue: leftYAxisMinMax.min,
    leftYAxisMaxValue: leftYAxisMinMax.max,
    leftYAxisStepsSize: leftYAxisMinMax.stepSize,
    leftYAxisFormatString: getYAxisFormatString(vue, leftYAxis),
    leftYAxisMaxValueString: getYAxisMaxValueString(vue, leftYAxis),
    leftYAxisColor: COLOR_GRAPH_DARK_RED,

    rightYAxisMinValue: rightYAxisMinMax.min,
    rightYAxisMaxValue: rightYAxisMinMax.max,
    rightYAxisStepsSize: rightYAxisMinMax.stepSize,
    rightYAxisFormatString: getYAxisFormatString(vue, rightYAxis),
    rightYAxisMaxValueString: getYAxisMaxValueString(vue, rightYAxis),
    rightYAxisColor: isPanel ? COLOR_DRYING_GRAPH_INTENSITY : COLOR_GRAPH_DARK_BLUE,

    limitLines: limitLines,
  };

  return {
    chartData: chartData,
    options: options,
    metaData: metaData,
    sensorCombinations:
      sensorValueCombinationCollector.sortedSensorCombinations,
    powerConsumptionValues: powerConsumptionValues,
    weatherDataLoadFailed: weatherDataLoadFailed,
  };
}
